/**
 * The resident's notes, as one conversation (2026-10-05).
 *
 * The owner: "We need persistent note history similar to a chat history. The
 * girls are losing notes, and when adding notes it's moving them out of that
 * view they are on." No note was ever lost -- the note tables refuse updates
 * and deletes -- but each note belonged to one case, so a replacement, a
 * reopened lead or a duplicate row started with an empty history, and the
 * notes a PA wrote sat under History while the open-by-default "Notes" cell
 * showed Monday's text.
 *
 * This is every note on every case of the resident the reader may open, from
 * every desk the reader may see, oldest at the top and newest at the bottom:
 *
 *   orient     how many notes, from which desks, and what Monday said before
 *              eMerge (pinned, labelled history, never dated by us)
 *   understand who wrote each note, their role, when (relative, and the exact
 *              time on hover and to a screen reader), on which episode, what kind
 *   explore    earlier notes load upward as you scroll (or press the button);
 *              search runs on the server across the whole thread
 *   act        the composer is pinned to the bottom of the panel; Enter sends,
 *              Shift+Enter is a new line
 *   confirm    the note appears at once as "Sending…" and settles in place; a
 *              failure keeps the words with Retry (the same key, so a retry is
 *              never a second note), Copy, Edit and Discard. Until the server confirms it,
 *              the note lives in this browser under the signed-in user and the
 *              resident (noteStorage: 7 days at most, cleared at sign-out), so
 *              leaving the tab, the case or the panel never loses it. Once the
 *              server confirms it, it stays on screen even if the re-read
 *              after it fails. Announcements go through one polite status
 *              line; the list itself is not a live region (opening a thread
 *              must not read fifty notes aloud).
 *
 * Sending never reloads the list, changes the view, the lens, the page or the
 * panel: only this thread changes. Corrections are new notes that say which
 * note they correct; nothing here edits or deletes a recorded note.
 *
 * NO TYPED WORD IS EVER DISCARDED WITHOUT AN EXPLICIT DISCARD (fix round 4).
 * Edit on an unsent note never overwrites the box: words already in it that
 * are not that note's are set aside as a "Draft kept" item (Send / Edit /
 * Discard of its own). The edited note stays in the unsent list as "Editing",
 * with its original key, until it is sent or discarded, and the box remembers
 * which note it holds, so Edit, a reload and Send record it once.
 *
 * Fix round 5, the same rule across tabs and edits:
 *   - each tab has its own box (noteStorage: one draft key per tab), so a
 *     Send, Edit or keystroke in one tab never clears another's words; a box
 *     left by a tab that closed comes into the list as "Draft kept";
 *   - an edited note ALWAYS keeps its key, changed or not, so a first send
 *     that did land is never joined by a second record; if it did land, the
 *     edited words are kept as "Draft kept" and the thread says why;
 *   - a reader who may no longer write still sees her box's words, with Copy
 *     and Discard; emptying the box while editing puts the note back as it was;
 *     the panel moving to another case never retargets a note being edited.
 *
 * Fix round 8, NO NOTE IS EVER RECORDED TWICE across a sleeping tab:
 *   - a tab that sends the "Draft kept" copy of another tab's box leaves a text-free "sent" mark; the
 *     tab whose box it was wakes, finds the mark, does NOT write its words back, and says they were sent
 *     (with a link to the note). A copy still being sent or edited there is shown here, as that note;
 *   - with no copy and no mark, the words come back with a visible caution, and Send first looks for the
 *     very same words of hers in the newest notes and asks before sending them again;
 *   - a box is written back and read back before any copy of it goes;
 *   - after her sign-out (in any tab) nothing more is written from here: what it deleted stays deleted.
 *
 * Fix round 9: a mark speaks only for the box it names (a stale one never drops new words); the caution is
 * stored with the box and survives a reload; the cautioned check pages back past the newest 50; a box write
 * that fails is never taken for a saved box; and another person's session is no session of hers.
 *
 * Fix round 10: the caution goes wherever its words go -- set aside by Edit, kept from a closed tab's box, back
 * to the note being edited -- and Send or Retry on such a note asks first, as the box does; the banner leaves the
 * box with them. A "sent" mark (or a copy being sent elsewhere) gives up the box only when the screen holds
 * exactly what was saved: words typed while the box could not be saved stay, under a caution. A box write that
 * fails says so beside the box until one lands again.
 *
 * Fix round 12: "Send only the new part" is offered only for the part another tab really sent -- under a PARTIAL
 * caution, a note of hers of at least 20 characters that BEGINS these words at a word boundary, recorded no
 * earlier than 5 minutes before the caution -- and the question quotes it. A partial caution whose part is no
 * longer found (the box was edited) asks before sending. Words on this screen only (storage full) are held in the
 * page's memory too, so the thread closing in-app (another section, Prev/Next, the panel closed) never loses them.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Marker } from '../../components/tide';
import type { MarkerShape } from '../../components/tide';
import { useOptionalUser } from '../../contexts/UserContext';
import { advocateApi, freshKey, NoteKind, ResidentNote, ResidentNotesPage, NoteSource } from './advocateApi';
import {
  addToOutbox, adoptLeftoverDrafts, announceConfirmed, clearSentMarks, clearTabSentMarks, composerSavedAt, currentTabId, DraftAim, DraftEdit, dropConfirmed, forgetInMemory, forgetMemoryNote,
  heldInMemory, HeldBox, holdInMemory, isOwnDraftKey, replaceMemoryNote, isOwnOutboxKey, isPresenceKeyFor,
  isSignOutKeyFor, leaveSentMark, liveEditKeys, markThreadClosed, markThreadOpen, onNoteConfirmed, pruneExpiredNoteKeys, readComposer, readOutbox, readOutboxNote, removeFromOutbox,
  renewTabId, sentMarksFor, setOutboxCaution, setOutboxHold, StoredHold, StoredNote, StoredWhy, takenCopiesOf, writeComposer,
} from './noteStorage';
import { hasSessionFor, signedOutAt, signedOutSince } from '../../config/authRoutes';
import './notesThread.css';

/** Where a note came from: one colour, one shape, one word each (never colour alone). */
const SOURCE: Record<NoteSource | 'MONDAY', { label: string; color: string; marker: MarkerShape; what: string }> = {
  PA: { label: 'Patient Advocate', color: 'var(--tide-cat-pa)', marker: 'circle', what: 'Written in eMerge on a PA case.' },
  UMED: { label: 'UMED', color: 'var(--tide-cat-umed)', marker: 'diamond', what: 'Written by the UMED desk on the resident’s UMED.' },
  FINANCE: { label: 'Finance', color: 'var(--tide-cat-finance)', marker: 'triangle', what: 'Written by Finance on the resident’s account.' },
  MONDAY: { label: 'From Monday', color: 'var(--tide-cat-import)', marker: 'hatch', what: 'The board’s own text, kept as history. Not dated, never edited here.' },
};

const KIND_LABEL: Record<string, string> = {
  GENERAL: 'Note', VETTING: 'Vetting', UMED: 'UMED note', NOTE: 'Note', CALL: 'Call',
  PAYMENT_ARRANGEMENT: 'Payment arrangement', RECONCILIATION: 'Reconciliation',
};

const ZONE = 'America/New_York';
const exact = (iso: string) => new Date(iso).toLocaleString('en-US', { timeZone: ZONE, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const clock = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { timeZone: ZONE, hour: 'numeric', minute: '2-digit' });
const dayKey = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));

/** "just now", "12 min ago", "3 h ago", "yesterday", "4 days ago", then the date. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24 && dayKey(iso) === dayKey(new Date(now).toISOString())) return `${h} h ago`;
  const days = Math.round((Date.parse(`${dayKey(new Date(now).toISOString())}T00:00:00Z`) - Date.parse(`${dayKey(iso)}T00:00:00Z`)) / 86400000);
  if (days <= 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { timeZone: ZONE, month: 'short', day: 'numeric', year: 'numeric' });
}

function dayHeading(key: string, now: number = Date.now()): string {
  const today = dayKey(new Date(now).toISOString());
  const yesterday = dayKey(new Date(now - 86400000).toISOString());
  if (key === today) return 'Today';
  if (key === yesterday) return 'Yesterday';
  return new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export function initials(name: string | null | undefined): string {
  const parts = String(name || '').replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
}

/** The search words, marked where they occur. */
function Highlight({ text, q }: { text: string; q: string | null }) {
  if (!q) return <>{text}</>;
  const words = q.trim().split(/\s+/).filter(Boolean).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!words.length) return <>{text}</>;
  const re = new RegExp(`(${words.join('|')})`, 'ig');
  const parts = text.split(re);
  return <>{parts.map((p, i) => (i % 2 === 1 ? <mark key={i}>{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>))}</>;
}

/**
 * `stored`: this note is known to be in the browser's outbox, so its leaving the outbox means another
 * tab confirmed or discarded it. `hold`: why it waits when it is not simply unsent -- 'kept' (a draft
 * set aside, never sent) or 'editing' (its words are in the box below); `from` is what an editing
 * note returns to when the editing stops. `caution` (fix round 10): these words came from a box written back
 * under a caution (another tab may have sent them), so this note's Send checks the thread first.
 */
interface Pending {
  key: string; caseId: string; kind: NoteKind; note: string; state: 'sending' | 'failed'; error?: string; at: string; stored?: boolean;
  hold?: StoredHold; from?: 'kept'; why?: StoredWhy; caution?: number;
  /** Fix round 11: with a caution, PART of these words already went from another tab. */
  partial?: boolean;
}

/** Why a kept draft waits, in words (never colour alone): set aside by Edit, left by a closed tab, or not recorded after an earlier version. */
const KEPT_WHY: Record<StoredWhy | 'edit', string> = {
  edit: 'Set aside from the box when you edited another note. Not sent.',
  // Fix round 7: the other tab may only have gone quiet (asleep, frozen in the background), not closed.
  tab: 'From the note box of another tab or window that went quiet: closed, asleep or in the background. Not sent. If that tab wakes with the same words, it takes them back and this copy goes; send only one.',
  earlier: 'Not recorded: this note had already been recorded from an earlier send, with other words, another kind or on another case, so this version was not. Compare with the thread above, then send it as a new note or discard it.',
};

/**
 * How the server's copy of a replayed note differs from what was just sent (fix round 6): its words,
 * its kind and its case, in words the thread can say. Empty: it is this very note.
 */
export function replayDiffers(server: Pick<ResidentNote, 'note' | 'kind' | 'case_id' | 'episode'>, sent: { note: string; kind: string; caseId: string }): string[] {
  const out: string[] = [];
  if (String(server.note || '').trim() !== sent.note.trim()) out.push('with other words');
  if (server.kind !== sent.kind) out.push(`as ${server.kind === 'VETTING' ? 'a Vetting note' : 'a plain Note'}`);
  const onCase = server.case_id || server.episode?.case_id || null;
  if (onCase !== sent.caseId) out.push(`on ${server.episode?.label ? server.episode.label : 'another case'}`);
  return out;
}

/**
 * The outbox: every note that has been sent but not yet CONFIRMED, with its
 * idempotency key, kept in this browser under the signed-in user AND the
 * resident (noteStorage). A note whose send failed (a 429, a dropped
 * connection) survives the PA switching tab, case or closing the panel: when
 * the thread opens again it is there, "Not confirmed", with Retry -- the same
 * key, so a retry of a note that did land is answered with the first one and
 * never recorded twice. Another person signed in on the same browser never
 * sees it, and a sign-out clears it.
 */
/** How many pages of 50 a replay's check reads back looking for the recorded note (fix round 7). */
export const REPLAY_PAGES = 20;
export const UNCONFIRMED ='Not confirmed before you left this resident. Retry is safe: a note is never recorded twice.';
const fromStore = (list: StoredNote[]): Pending[] => list.map((p) => ({
  ...p, at: '', state: 'failed' as const, stored: true,
  error: p.hold === 'kept' || (p.hold === 'editing' && p.from === 'kept') ? undefined : UNCONFIRMED,
}));
/** What an editing note goes back to when the editing stops: a kept draft, or an unsent note. */
const settle = (p: Pending): Pending => ({ ...p, hold: p.from === 'kept' ? 'kept' : undefined, from: undefined });

/**
 * What the thread opens with: the outbox, and the box's words. An "Editing" note whose words are not
 * the box's any more (the box was emptied, another tab) goes back to what it was -- nothing is lost,
 * its words are in the list -- and the box knows the note it holds even if the list lost it.
 */
interface Opened {
  pending: Pending[]; text: string; edit: DraftEdit | null; savedAt: number; caution: number; partial: boolean;
  /** Fix round 12: what storage holds for the box (the box itself may come from memory). */
  stored: string;
  /** Fix round 12: the box's words came from this page's memory (they could not be stored): still unsaved. */
  fromMemory: boolean;
  aim: { caseId?: string; kind?: NoteKind } | null;
}
function openWith(userId: string | null, patientId: string | null, tab: string, actingCaseId: string): Opened {
  if (!patientId) return { pending: [], text: '', edit: null, savedAt: 0, caution: 0, partial: false, stored: '', fromMemory: false, aim: null };
  // This tab has the thread open (so no other tab takes its box as a leftover), and any box a closed
  // tab left for this resident comes into the outbox as "Draft kept" (fix round 5: one box per tab).
  markThreadOpen(userId, patientId, tab);
  // Fix round 12: words this page held in memory only (storage full) when the thread last closed in-app (another
  // section, Prev/Next, the panel closed). The box's words there are newer than storage's; a note that was on
  // screen only comes back as it was, "Not saved on this computer", with its own key (a Retry is never a second note).
  const mem = heldInMemory(userId, patientId, tab);
  // Fix round 9: a thread just opened holds no words in memory, so no "sent" mark left for this tab's
  // boxes before now can be about anything it holds: they go, unread, before they can speak for new words.
  // Fix round 13: unless this page DOES hold words for a box storage had: a mark naming that box is exactly what
  // the taken-box check on reopen must read (another tab sent it while the thread was closed).
  if (!(mem && mem.box && mem.box.text.trim() && (mem.box.stored || '').trim())) clearTabSentMarks(userId, patientId, tab);
  adoptLeftoverDrafts(userId, patientId, tab, { caseId: actingCaseId });
  const draft = readComposer(userId, patientId, Date.now(), tab);
  const memBox: HeldBox | null = mem && mem.box && mem.box.text.trim() && mem.box.text !== draft.text ? mem.box : null;
  const boxEdit = memBox ? memBox.edit : (draft.text ? draft.edit : null);
  // A note another open tab's box is editing stays "Editing": that box holds it.
  const heldElsewhere = liveEditKeys(userId, patientId, tab);
  const fromDisk = fromStore(readOutbox(userId, patientId));
  const fromMem: Pending[] = (mem ? mem.notes : []).filter((n) => !fromDisk.some((p) => p.key === n.key)).map((n) => ({ ...fromStore([n])[0], stored: false }));
  const list = [...fromDisk, ...fromMem].map((p) => {
    if (p.hold !== 'editing' || (boxEdit && boxEdit.key === p.key) || heldElsewhere.has(p.key)) return p;
    setOutboxHold(userId, patientId, p.key, p.from === 'kept' ? 'kept' : null);
    return settle(p);
  });
  if (memBox) {
    return {
      // Fix round 13: the box storage held for these words, and when it was saved -- NOT what storage holds now,
      // which is empty if another tab took it meanwhile -- so the taken-box check (reconcileBox) runs on reopen.
      pending: list, text: memBox.text, edit: memBox.edit, savedAt: memBox.storedAt || 0,
      caution: memBox.caution && memBox.caution > 0 ? memBox.caution : 0, partial: Boolean(memBox.caution && memBox.partial),
      stored: memBox.stored || '', fromMemory: true, aim: { caseId: memBox.caseId, kind: memBox.kind },
    };
  }
  return {
    pending: list, text: draft.text, edit: draft.text ? draft.edit : null, savedAt: draft.text ? composerSavedAt(userId, patientId, Date.now(), tab) : 0,
    // Fix round 9: a box written back under a caution keeps it across a reload, with its duplicate check.
    caution: draft.text && draft.caution ? draft.caution : 0,
    // Fix round 11: and whether part of its words already went from another tab.
    partial: Boolean(draft.text && draft.caution && draft.partial),
    stored: draft.text, fromMemory: false, aim: null,
  };
}

/**
 * How many notes a cautioned Send reads per page to look for the very same words (fix round 8). Fix round 9:
 * it pages back (at most REPLAY_PAGES pages) until it reaches notes older than a day before the box was
 * last saved -- a copy of these words cannot have been recorded before they were written -- or the thread ends.
 */
export const SAME_NOTE_WINDOW = 50;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Said beside a box written back by a waking tab that found no copy and no "sent" mark (fix round 8). */
export const CAUTION = 'Another tab may have sent these words. Check the thread before sending.';
/** Fix round 11: the caution when PART of the words went from another tab (a box taken and sent as saved, then typed on). */
export const CAUTION_PARTIAL = 'Another tab sent, or is sending, part of these words. Check the thread before sending.';
/** Said beside the box while its last write did not land (fix round 10): the words live on this screen only. */
export const UNSAVED = 'Your typing couldn’t be saved on this computer (storage is full or blocked). Keep this page open, or send or copy your words.';
/** Fix round 11: how long the typing rests before a box that is saved again takes the "couldn't be saved" notice down. */
export const UNSAVED_SETTLE_MS = 1500;
/** Fix round 11: said beside the box while an unsent note above could not be saved (the box itself may be empty). */
export const UNSAVED_ITEMS = 'An unsent note above couldn’t be saved on this computer (storage is full or blocked). Keep this page open, or send or copy it.';
/** Fix round 11: said on an unsent note whose words are on this screen only. */
export const NOT_STORED = 'Not saved on this computer (storage is full or blocked): these words are only on this screen. Keep this page open, or copy them.';
/** Fix round 11: what leaving the page says while any words are on this screen only (beforeunload). */
export const LEAVE_WARNING = 'You have words that aren’t saved on this computer. Leave anyway?';
/**
 * What a cautioned Send found: these words already in the thread, already waiting unsent, or no way to tell.
 * Fix round 11: 'part' -- a note of hers since the caution whose words begin these (a box taken and sent as
 * saved, then typed on): `text` its words, `rest` what follows them here.
 * Fix round 12: a 'part' is looked for only under a PARTIAL caution, and only as the START of these words, at
 * a word boundary, at least PART_MIN_CHARS long, recorded no earlier than PART_SLACK_MS before the caution --
 * an unrelated earlier note ("Called.", "ok") used to be offered as the part, and "Send only the new part"
 * dropped the words it matched. 'partMissing': the caution is partial, yet no such note was found (the box was
 * edited since): the PA is asked rather than the sent part being recorded again in silence.
 */
type SameNote = { where: 'thread'; noteId: string; at: string } | { where: 'unsent'; key: string } | { where: 'unknown' }
  | { where: 'part'; noteId: string; at: string; text: string; rest: string } | { where: 'partMissing' };
/** Fix round 12: the shortest recorded note taken for the part of a box another tab sent. */
export const PART_MIN_CHARS = 20;
/** Fix round 12: how long before the caution a recorded note may be and still be that part (clock skew). */
export const PART_SLACK_MS = 5 * 60 * 1000;
const wordChar = /[A-Za-z0-9À-ɏ]/;
/** `t` begins `words` and ends at a word boundary there, with more words after it. */
export function isLeadingPart(t: string, words: string): boolean {
  if (t.length < PART_MIN_CHARS || t.length >= words.length || !words.startsWith(t)) return false;
  return !(wordChar.test(t.charAt(t.length - 1)) && wordChar.test(words.charAt(t.length))) && Boolean(words.slice(t.length).trim());
}
/** A recorded note's words, quoted in a question: whole when short, else its start and its end. */
export function quoteOf(t: string): string {
  const flat = t.replace(/\s+/g, ' ').trim();
  if (flat.length <= 90) return flat;
  const head = flat.slice(0, 60);
  const tail = flat.slice(-25);
  const h = head.lastIndexOf(' ') > 30 ? head.slice(0, head.lastIndexOf(' ')) : head;
  const t2 = tail.indexOf(' ') >= 0 && tail.indexOf(' ') < 12 ? tail.slice(tail.indexOf(' ') + 1) : tail;
  return `${h} … ${t2}`;
}
/** Fix round 12: asked when a partial caution's sent part is not at the start of the words any more. */
export const PART_MISSING = 'The part sent from another tab couldn’t be found in these words. Send all, or don’t send?';
const sendError = (r: { status?: number; code?: string; error?: string; message?: string; retryAfterSeconds?: number }): string => {
  if (r.status === undefined) return 'Could not reach the server, so this is not confirmed. Retry is safe: a note is never recorded twice.';
  if (r.status === 429) {
    const min = Math.max(1, Math.ceil((r.retryAfterSeconds || 900) / 60));
    return `Too many requests in the last few minutes. Nothing was recorded. Retry in about ${min} minute${min === 1 ? '' : 's'}; your words are kept.`;
  }
  if (r.status >= 500) return 'The server failed while recording this. Nothing was recorded.';
  return r.error || r.message || 'That note did not go through.';
};

export interface NotesThreadProps {
  /** The resident. Null when the case is linked to nobody: there is no thread to read. */
  patientId: string | null;
  /** The case the panel is open on: the composer's default target. */
  actingCaseId: string;
  /** add_note is held AND its group open (scope.can). The thread also says when the reader may only read. */
  canWrite: boolean;
  /** Tell the shell a note landed, so the row's "Latest note" can be patched in place (no reload). */
  onNoteAdded?: (caseId: string, note: { kind: NoteKind; preview: string; recorded_at: string }) => void;
  /** Focus the composer when the thread opens (the row's "Add note"). */
  focusComposer?: boolean;
  /**
   * The signed-in user, whose unsent notes this browser may keep. Defaults to
   * the signed-in session's user (UserContext). Without one, nothing is stored: the words live in
   * the field only.
   */
  userId?: string | null;
  /** This tab's id for its own note box (noteStorage.currentTabId). Tests pass one to stand for a second tab. */
  tabId?: string;
}

/** Which conversation a note belongs to: its desk and its case. A divider marks each change. */
const strandOf = (n: ResidentNote) => `${n.source}:${n.episode.case_id || ''}`;

export default function ResidentNotesThread({ patientId, actingCaseId, canWrite, onNoteAdded, focusComposer = false, userId: userIdProp, tabId: tabIdProp }: NotesThreadProps) {
  const session = useOptionalUser();
  const userId = userIdProp !== undefined ? userIdProp : (session?.user?.id || null);
  // This tab's own note box (fix round 5): another tab never writes or clears it, nor this tab theirs.
  const tabRef = useRef<string>(tabIdProp || currentTabId());
  // Fix round 8: this panel began showing her work now. A sign-out of hers after this moment (in this tab
  // or any other) stops every write from here: what her sign-out deleted is never written back.
  // Fix round 11: a sign-out marker dated in the future (written while the clock ran ahead) is re-dated to just
  // before now FIRST (signedOutAt), so a page opened after that sign-out is never taken for one that was open
  // during it -- it used to refuse to store anything and say "You signed out in another tab".
  const mountedAt = useRef<number>(0);
  if (mountedAt.current === 0) { if (userId) signedOutAt(userId); mountedAt.current = Date.now(); }
  const storeOff = useRef(false);
  const live = (): boolean => {
    if (storeOff.current) return false;
    if (userId && signedOutSince(userId, mountedAt.current)) {
      storeOff.current = true;
      // However it was found (a storage event, a wake, a keystroke, a Send), the panel says so -- once.
      window.setTimeout(() => afterSignOutNow.current(), 0);
      return false;
    }
    return true;
  };
  /** The user to store under; null once she signed out, so noteStorage stores (and reads) nothing more. */
  const su = (): string | null => (live() ? userId : null);
  // Expired note keys (anyone's, judged by the name alone) and the old dayless keys go, unread,
  // before anything is read. Another person's live keys are left: they are hers to come back to.
  const [swept] = useState(() => { pruneExpiredNoteKeys(); return true; });
  const [page, setPage] = useState<ResidentNotesPage | null>(null);
  const [items, setItems] = useState<ResidentNote[]>([]); // oldest first
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [older, setOlder] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState<string | null>(null);
  const [qDraft, setQDraft] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInput = useRef<HTMLInputElement | null>(null);
  // An active search keeps its field open: the reader must always see what is filtering the thread.
  const searchShown = searchOpen || Boolean(q);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [opened] = useState(() => (swept ? openWith(userId, patientId, tabRef.current, actingCaseId) : openWith(null, null, tabRef.current, actingCaseId)));
  const [pending, setPending] = useState<Pending[]>(opened.pending);
  const [text, setText] = useState(opened.text);
  // Notes this panel saw confirmed: they are the reader's own, shown with the same "You" as while sending.
  const mineLocal = useRef<Set<string>>(new Set());
  // The box opens on the kind and case of the note it holds, if it holds one.
  // Fix round 12: words brought back from this page's memory keep where they were aimed.
  const [kind, setKind] = useState<NoteKind>(opened.edit ? opened.edit.kind : (opened.aim?.kind === 'VETTING' ? 'VETTING' : 'GENERAL'));
  const [target, setTarget] = useState<string>(opened.edit ? opened.edit.caseId : (opened.aim?.caseId || actingCaseId));
  const [unseen, setUnseen] = useState(0);
  // One polite announcement at a time, outside the list: a send, a failure, earlier notes arriving.
  const [said, setSaid] = useState('');
  // Roving focus for the per-note action: one tab stop for the whole thread, arrows move between notes.
  const [actAt, setActAt] = useState<number>(-1);
  const scroller = useRef<HTMLDivElement | null>(null);
  const actButtons = useRef<Array<HTMLButtonElement | null>>([]);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const composer = useRef<HTMLTextAreaElement | null>(null);
  const keepFromBottom = useRef<number | null>(null);
  const stickToBottom = useRef(true);
  /** Where the list was put on its newest note; null once the reader scrolls away from there. */
  const landedAt = useRef<number | null>(null);
  const now = Date.now();

  // The outbox is changed one note at a time against what is in storage NOW
  // (addToOutbox on send, dropConfirmed on confirm, removeFromOutbox on Edit),
  // never by writing this panel's whole list: a second tab on the same
  // resident would otherwise put back a note this one confirmed (fix round 3).
  // The unsent note whose words are in the box (Edit), remembered with the draft: re-sent UNCHANGED it
  // keeps its key, so it is never recorded twice, even after a reload.
  const editOf = useRef<DraftEdit | null>(opened.edit);
  const pendingNow = useRef<Pending[]>(pending);
  pendingNow.current = pending;
  const textNow = useRef(text);
  textNow.current = text;
  // What this tab last read from or wrote to its own box. Anything else found there was written by a
  // duplicate of this tab (same tab id): those words are that tab's, and this tab moves to a new id.
  // Fix round 12: what storage holds, which is not the box's words when they came back from memory.
  const boxSeen = useRef<string>(opened.stored);
  // Where the box is aimed, for a box written back from a handler that runs outside a render.
  const aimNow = useRef<DraftAim>({});
  // Said beside the box when a waking tab finds its words were also kept as a draft by another tab.
  const [boxNote, setBoxNote] = useState<string | null>(null);
  // When this tab last saved its box (the time in the stored value): it names the box another tab took.
  const boxAt = useRef<number>(opened.savedAt);
  // Fix round 8: the box was written back by a waking tab that found no copy and no "sent" mark, so another
  // tab MAY have sent these words. Said beside the box; Send first looks for the same words in the thread.
  // Fix round 9: the caution is a time (when the box was last saved before it was taken: no copy of its words
  // can be older), stored WITH the box, so a reload keeps the caution and the check (0: no caution).
  const [caution, setCautionState] = useState(opened.caution > 0);
  const cautionNow = useRef<number>(opened.caution);
  // Fix round 11: part of the box's words already went from another tab ('partial'), kept with the caution.
  const [cautionPartial, setCautionPartialState] = useState(opened.partial);
  const partialNow = useRef<boolean>(opened.partial);
  const setCaution = (on: boolean, since = 0, partial = false) => {
    cautionNow.current = on ? (since > 0 ? (cautionNow.current ? Math.min(cautionNow.current, since) : since) : (cautionNow.current || 1)) : 0;
    partialNow.current = on ? (partialNow.current || partial) : false;
    setCautionState(on);
    setCautionPartialState(partialNow.current);
  };
  /** Where the box is aimed, with its caution: what every write of the box carries. */
  const withCaution = (aim: DraftAim): DraftAim => (cautionNow.current ? { ...aim, caution: cautionNow.current, ...(partialNow.current ? { partial: true } : {}) } : aim);
  // Fix round 8: the box's words were sent from another tab while this one slept (the note's id, to show it).
  const [sentHere, setSentHere] = useState<{ noteId: string } | null>(null);
  // Fix round 8: a cautioned Send found the same words: it asks before sending them again.
  const [sameAsk, setSameAsk] = useState<SameNote | null>(null);
  const [checking, setChecking] = useState(false);
  // Fix round 10: the same question for an unsent note that carries a caution (Send or Retry on it), shown on it.
  const [itemAsk, setItemAsk] = useState<{ key: string; same: SameNote } | null>(null);
  const [checkingKey, setCheckingKey] = useState<string | null>(null);
  const checkingKeyNow = useRef<string | null>(null);
  // Fix round 10: the last write of the box did not land (a full quota, blocked storage): the words on screen
  // are newer than the box. Said beside the box until a write lands again.
  const [unsaved, setUnsavedState] = useState(opened.fromMemory);
  const unsavedNow = useRef(opened.fromMemory);
  const unsavedTimer = useRef<number | null>(null);
  const setUnsavedFlag = (on: boolean): void => {
    if (unsavedNow.current === on) return;
    unsavedNow.current = on;
    setUnsavedState(on);
    if (on) setSaid(UNSAVED);
  };
  /**
   * What a write of the box came to (fix round 11). A write that failed says so at once. One that landed does
   * NOT take the notice down by itself: under a quota that fails every other write it used to flap on and off
   * with each keystroke. The notice goes only once the typing has rested (UNSAVED_SETTLE_MS) AND the box in
   * storage then holds exactly what is on screen.
   */
  const setUnsaved = (on: boolean, now = false): void => {
    if (unsavedTimer.current !== null) { window.clearTimeout(unsavedTimer.current); unsavedTimer.current = null; }
    if (on) { setUnsavedFlag(true); return; }
    if (!unsavedNow.current) return;
    if (now) { setUnsavedFlag(false); return; }
    unsavedTimer.current = window.setTimeout(() => {
      unsavedTimer.current = null;
      const u = su();
      const stored = u && patientId ? readComposer(u, patientId, Date.now(), tabRef.current).text : '';
      if (!u || stored === textNow.current || (!stored && !textNow.current.trim())) setUnsavedFlag(false);
    }, UNSAVED_SETTLE_MS);
  };
  useEffect(() => () => { if (unsavedTimer.current !== null) window.clearTimeout(unsavedTimer.current); }, []);
  /** Words that leave the box under its caution keep it in the list (fix round 10): their Send asks first too. */
  const cautionItem = (key: string, since: number, partial = false): void => {
    if (!(since > 0)) return;
    setOutboxCaution(su(), patientId, key, since, Date.now(), partial);
    setPending((list) => list.map((x) => (x.key === key ? { ...x, caution: x.caution ? Math.min(x.caution, since) : since, partial: Boolean(x.partial || partial) || undefined } : x)));
  };
  /** The caution did its work (the thread was checked, or the PA said "Send anyway"): this note sends plainly now. */
  const uncautionItem = (key: string): void => {
    setOutboxCaution(su(), patientId, key, null);
    setPending((list) => list.map((x) => (x.key === key && x.caution ? { ...x, caution: undefined, partial: undefined } : x)));
  };
  /**
   * Fix round 11: unsent notes whose words could not be written here (a full quota) are tried again whenever a
   * write lands: once stored, they survive a reload and their "Not saved on this computer" line goes. Only a
   * note that is not being sent right now (one being sent may be confirmed and dropped meanwhile).
   */
  const storeUnstored = (): void => {
    const u = su();
    if (!u || !patientId) return;
    const waiting = pendingNow.current.filter((x) => x.stored === false && x.state === 'failed');
    if (!waiting.length) return;
    const landed: string[] = [];
    waiting.forEach((x) => {
      addToOutbox(u, patientId, {
        key: x.key, caseId: x.caseId, kind: x.kind === 'VETTING' ? 'VETTING' : 'GENERAL', note: x.note,
        ...(x.hold ? { hold: x.hold } : {}), ...(x.hold === 'editing' && x.from ? { from: x.from } : {}), ...(x.hold && x.why ? { why: x.why } : {}),
        ...(x.caution ? { caution: x.caution, ...(x.partial ? { partial: true } : {}) } : {}),
      });
      const back = readOutboxNote(u, patientId, x.key);
      if (back && back.note === x.note) landed.push(x.key);
    });
    if (landed.length) setPending((list) => list.map((x) => (landed.includes(x.key) ? { ...x, stored: true } : x)));
  };
  // Fix round 8: she signed out in another tab. `gone`: no session is left, so her words left the screen too.
  const [signedOut, setSignedOut] = useState<{ gone: boolean } | null>(null);
  // The note "Show the note" brought into view (focused, marked).
  const [foundId, setFoundId] = useState<{ id: string; n: number } | null>(null);

  /** The box's words went elsewhere (sent, or being sent or edited by another tab): this box lets them go. */
  const giveUpBox = (): void => {
    const e = editOf.current;
    if (e) {
      editOf.current = null;
      const u = su();
      const n = u && patientId ? readOutboxNote(u, patientId, e.key) : null;
      if (n && n.hold === 'editing') setOutboxHold(u, patientId, e.key, n.from === 'kept' ? 'kept' : null);
      setPending((list) => list.map((x) => (x.key === e.key && x.hold === 'editing' ? settle(x) : x)));
    }
    setText('');
    textNow.current = '';
    boxSeen.current = '';
    boxAt.current = 0;
    setCaution(false);
    setSameAsk(null);
    setBoxNote(null);
  };

  /**
   * A tab that was quiet (frozen, asleep, in the background) wakes, or types, or sends, and finds its box
   * gone: another tab took it as left behind (fix round 7). What it does depends on what became of the
   * words there (fix round 8), and it always says so:
   *   - SENT from there (a text-free "sent" mark names this box): NOT written back -- a Send here would
   *     record them twice. The thread says so and links to the note;
   *   - being sent or edited there (the copy is in the outbox, no longer "Draft kept"): not written back
   *     either; the copy shows in this tab's unsent notes, where a Retry is the same note;
   *   - still "Draft kept" there: written back, READ BACK, and only then does a copy with the same words go
   *     (other words: both kept, and said);
   *   - nothing at all (the copy was discarded, or the mark could not be written or has expired): written
   *     back with a visible caution, and Send first checks the thread for these very words.
   * Never after she signed out (su() is null): nothing is written back then.
   */
  type Woke = 'off' | 'here' | 'restored' | 'partial' | 'sent' | 'elsewhere';
  const reconcileBox = (): Woke => {
    const u = su();
    if (!u || !patientId) return 'off';
    const mine = textNow.current;
    if (!mine.trim()) return 'here';
    const tab = tabRef.current;
    if (readComposer(u, patientId, Date.now(), tab).text) return 'here'; // the box is still here
    // When the box that was taken was last saved: no copy of its words can have been recorded before it.
    const since = boxAt.current || 1;
    markThreadOpen(u, patientId, tab);
    const taken = takenCopiesOf(u, patientId, tab);
    const kept = taken.filter((c) => c.hold === 'kept' && c.why === 'tab');
    const busy = taken.filter((c) => !(c.hold === 'kept' && c.why === 'tab'));
    const marks = !kept.length && !busy.length ? sentMarksFor(u, patientId, tab, boxAt.current) : [];
    // Fix round 10: the other tab took (and sent, or is sending) the box AS SAVED. Words on screen that never
    // reached the box -- typed while its writes failed (a full quota) -- are not those words: they are given
    // up only when the screen holds exactly what was saved. Otherwise the box is written back below, under a
    // caution, and the PA is told part of it went from another tab.
    const sameAsSaved = mine.trim() === boxSeen.current.trim();
    const partial = !sameAsSaved && (busy.length > 0 || marks.length > 0);
    if (busy.length && sameAsSaved) {
      giveUpBox();
      const fresh = fromStore(busy);
      stickToBottom.current = true;
      setPending((list) => [...list.filter((x) => !fresh.some((y) => y.key === x.key)), ...fresh]);
      setSaid('While this tab was asleep, another tab took the words in this box and is sending or editing them there. They are in your unsent notes above as that same note, so a Retry never records them twice.');
      return 'elsewhere';
    }
    if (marks.length && sameAsSaved) {
      clearSentMarks(u, patientId, marks);
      giveUpBox();
      setSentHere({ noteId: marks[0].noteId });
      setSaid('These words were sent from another tab while this tab was asleep, so they were not put back in the box.');
      refreshNow.current().catch(() => { /* the note is the server's; the next open reads it */ });
      return 'sent';
    }
    // Written back BEFORE any copy goes, and read back: a copy goes only once the box holds these words again.
    // With no copy left at all, the box is written back WITH its caution (fix round 9), so a reload keeps it.
    // Fix round 10: so is a box part of which went from another tab (`partial`): its caution dates from when
    // the box was last saved (or, unknown, from the mark's own time).
    if (partial) {
      if (marks.length) clearSentMarks(u, patientId, marks);
      // Fix round 11: the caution carries the partial fact, wherever these words go (Edit, a closed tab's box).
      setCaution(true, boxAt.current || marks[0]?.at || 1, true);
      if (busy.length) {
        const fresh = fromStore(busy);
        stickToBottom.current = true;
        setPending((list) => [...list.filter((x) => !fresh.some((y) => y.key === x.key)), ...fresh]);
      }
    } else if (!kept.length) setCaution(true, since);
    const at = Date.now();
    writeComposer(u, patientId, mine, editOf.current, at, tab, withCaution(aimNow.current));
    const back = readComposer(u, patientId, Date.now(), tab).text === mine;
    if (back) { boxAt.current = at; boxSeen.current = mine; }
    // A wake's write-back is one deliberate write, read back: when it landed, the screen IS the stored box,
    // so the notice goes at once (the rest-then-check rule is for keystrokes under a flaky quota).
    setUnsaved(!back, true);
    const same = back ? kept.filter((c) => c.note.trim() === mine.trim()) : [];
    const shown = kept.filter((c) => !same.includes(c));
    same.forEach((c) => removeFromOutbox(u, patientId, c.key));
    if (same.length) setPending((list) => list.filter((x) => !same.some((c) => c.key === x.key)));
    // A note this box was editing that the other tab put back in the list: it is being edited here again.
    const e = editOf.current;
    if (e && back) {
      const n = readOutboxNote(u, patientId, e.key);
      if (n && n.hold !== 'editing') {
        setOutboxHold(u, patientId, e.key, 'editing', n.hold === 'kept' ? 'kept' : null);
        setPending((list) => list.map((x) => (x.key === e.key ? { ...x, hold: 'editing', from: n.hold === 'kept' ? 'kept' : undefined, state: 'failed' } : x)));
      }
    }
    if (partial) {
      if (shown.length) {
        const fresh = fromStore(shown);
        setPending((list) => [...list, ...fresh.filter((x) => !list.some((y) => y.key === x.key))]);
      }
      const msg = marks.length
        ? 'While this tab was asleep, another tab sent the words this box last saved. The box still holds everything on this screen, including what you typed after that (it could not be saved). Check the thread, take out what was already sent, then send the rest.'
        : 'While this tab was asleep, another tab took the words this box last saved and is sending or editing them there (in your unsent notes above). The box still holds everything on this screen, including what you typed after that (it could not be saved). Take out what is already above, then send the rest.';
      setBoxNote(msg);
      setSaid(msg);
      return 'partial';
    }
    if (shown.length) {
      const fresh = fromStore(shown);
      // The kept draft is brought into view, just above the box the notice sits on.
      stickToBottom.current = true;
      setPending((list) => [...list, ...fresh.filter((x) => !list.some((y) => y.key === x.key))]);
      const msg = back
        ? 'While this tab was asleep, another tab kept earlier words from this box as a draft. Both are kept: the draft just above, these words in the box. Send one, and discard the other if it is the same note.'
        : 'While this tab was asleep, another tab kept the words from this box as a draft, and this tab could not save its box again (storage is full or blocked). The draft just above holds them; the box below still shows them. Send one, and discard the other.';
      setBoxNote(msg);
      setSaid(msg);
    } else if (same.length) {
      setSaid('This tab was asleep, and another tab had kept this box’s words as a draft. That copy was removed: your words are in the box, once.');
    } else {
      setSaid(CAUTION);
    }
    return 'restored';
  };

  const reconcileNow = useRef(reconcileBox);
  reconcileNow.current = reconcileBox;
  // refreshNewest is defined below; a waking tab reaches it through this ref.
  const refreshNow = useRef<() => Promise<void>>(async () => { /* set below */ });

  /** Save the box's words under THIS tab (compare-and-set against a duplicate of this tab). */
  const saveBox = (next: string, edit: DraftEdit | null, aim: DraftAim = {}): void => {
    const u = su();
    if (!u || !patientId) { boxSeen.current = next; return; }
    const cur = readComposer(u, patientId, Date.now(), tabRef.current).text;
    // The box this tab last wrote has gone: another tab took it while this one was quiet (fix round 7).
    // Settled first, only while words are being typed (a box being emptied has nothing to settle).
    if (!cur && next.trim() && boxSeen.current.trim()) {
      const was = textNow.current.trim();
      const since = boxAt.current || 1;
      const woke = reconcileBox();
      if (woke === 'sent' || woke === 'elsewhere') {
        // Those words went from another tab. Words typed since are new, and stay -- with the caution,
        // since they begin with what was sent.
        if (next.trim() === was) return;
        setText(next);
        setCaution(true, since, true);
      }
    }
    if (cur && cur !== boxSeen.current && cur !== next) {
      tabRef.current = renewTabId();
      markThreadOpen(u, patientId, tabRef.current);
    }
    const at = Date.now();
    // Fix round 9: only a write that landed is taken as this tab's box. A write that failed (a full quota)
    // left the earlier box in place; this tab goes on knowing THAT box, so it never takes its own box for
    // a duplicate tab's, nor names a box it never saved.
    if (writeComposer(u, patientId, next, edit, at, tabRef.current, withCaution(aim))) {
      boxAt.current = next ? at : 0;
      boxSeen.current = next;
      setUnsaved(false);
      // There is room again: an unsent note that could not be kept here is kept now (fix round 11).
      storeUnstored();
    } else {
      const left = readComposer(u, patientId, Date.now(), tabRef.current).text;
      if (left !== boxSeen.current) { boxSeen.current = left; boxAt.current = left ? composerSavedAt(u, patientId, Date.now(), tabRef.current) : 0; }
      // Fix round 10: said, politely, beside the box -- the words on screen are newer than what is kept here.
      setUnsaved(Boolean(next.trim()));
    }
  };

  /** She signed out (in another tab): nothing more is stored here; with no session left, her words leave the screen. */
  const afterSignOut = (): void => {
    storeOff.current = true;
    // Fix round 12: nothing of hers stays in this page's memory either.
    forgetInMemory(userId);
    if (signedOutNow.current) return;
    // Fix round 9: "a session is left" means HER session. A token of whoever signed in after her (another
    // user at this computer) is no session of hers: her words leave the screen as if none were left.
    const gone = !hasSessionFor(userId);
    signedOutNow.current = true;
    if (gone) {
      editOf.current = null;
      setText('');
      textNow.current = '';
      boxSeen.current = '';
      setPending([]);
      setBoxNote(null);
      setCaution(false);
      setSameAsk(null);
      setSentHere(null);
      setUnsaved(false);
    }
    setSignedOut({ gone });
    setSaid(gone
      ? 'You signed out in another tab. The unsent words on this computer were deleted with the sign-out.'
      : 'You signed out in another tab, so nothing here is kept on this computer any more. Copy any words you need before you leave.');
  };
  const signedOutNow = useRef(false);
  const afterSignOutNow = useRef(afterSignOut);
  afterSignOutNow.current = afterSignOut;

  // While the thread is open this tab says so every 30 s, so another tab never takes its box as left
  // behind; closing the thread or leaving the page says it is closed (its box is then at rest, and
  // the next thread to open moves it into the outbox as "Draft kept").
  useEffect(() => {
    if (!userId || !patientId) return undefined;
    markThreadOpen(userId, patientId, tabRef.current);
    const beat = window.setInterval(() => markThreadOpen(su(), patientId, tabRef.current), 30000);
    const gone = () => markThreadClosed(userId, patientId, tabRef.current);
    // Waking (fix round 7): back from the page cache, made visible again, or resumed after the browser
    // froze it -- say the thread is open, then check whether another tab took this box while it was quiet.
    // Fix round 8: never after she signed out -- a frozen tab may have missed the sign-out while it slept.
    const back = () => {
      if (!live()) { afterSignOutNow.current(); return; }
      markThreadOpen(userId, patientId, tabRef.current);
      reconcileNow.current();
    };
    const seen = () => { if (document.visibilityState === 'visible') back(); };
    window.addEventListener('pagehide', gone);
    window.addEventListener('pageshow', back);
    document.addEventListener('visibilitychange', seen);
    document.addEventListener('resume', back);
    return () => {
      window.clearInterval(beat);
      window.removeEventListener('pagehide', gone);
      window.removeEventListener('pageshow', back);
      document.removeEventListener('visibilitychange', seen);
      document.removeEventListener('resume', back);
      markThreadClosed(userId, patientId, tabRef.current);
    };
  }, [userId, patientId]);

  const atBottom = () => {
    const el = scroller.current;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };
  const toBottom = useCallback(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
    setUnseen(0);
  }, []);

  const load = useCallback(async (search: string | null) => {
    if (!patientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const r = await advocateApi.residentNotes(patientId, { q: search, limit: 50 });
      if (!r.success || !r.data) { setErr(r.status === 403 ? 'You cannot read this resident’s notes.' : sendError(r)); return; }
      setPage(r.data);
      setItems([...r.data.items].reverse());
      setCursor(r.data.next_cursor);
      stickToBottom.current = true;
    } catch { setErr('Could not reach the server, so the notes are not shown.'); } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(q); }, [load, q]);
  // A different case of the same resident is still the same thread; only the default target follows the panel.
  // Never while a note is being edited: the box holds THAT note, on its own case (fix round 5).
  const actingWas = useRef(actingCaseId);
  useEffect(() => {
    if (actingWas.current === actingCaseId) return;
    actingWas.current = actingCaseId;
    if (!editOf.current) setTarget(actingCaseId);
  }, [actingCaseId]);
  useEffect(() => { if (focusComposer && composer.current) composer.current.focus(); }, [focusComposer, loading]);

  // The docked panel's box grows with the words up to its CSS cap (about four lines). Browsers with
  // `field-sizing: content` do it in CSS; for the rest it is measured here.
  useLayoutEffect(() => {
    const el = composer.current;
    if (!el || !el.closest('.notes-mode')) return;
    try { if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content')) return; } catch { /* measure below */ }
    el.style.height = 'auto';
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  // Land on the newest note: the bottom of the list, unless the newest note is taller than the room.
  const landNewest = (el: HTMLDivElement) => {
    el.scrollTop = el.scrollHeight;
    // A newest note taller than the room under the sticky day band opens at its top, so who wrote
    // it is never hidden under "Today"; the rest of it is a scroll (or "Jump to newest") away.
    // A joined note has no author line of its own: aim for the note that opens its run, if the
    // whole run fits under the band, else for the newest note's own first line.
    const msgs = Array.from(el.querySelectorAll('.pa-msg')) as HTMLElement[];
    const last = msgs.length ? msgs[msgs.length - 1] : null;
    const band = last ? last.closest('.pa-thread-day')?.querySelector('.pa-thread-dayhead') : null;
    if (last && band) {
      const bandH = band.getBoundingClientRect().height;
      let start = msgs.length - 1;
      while (start > 0 && msgs[start].classList.contains('joined')) start -= 1;
      const runFits = last.getBoundingClientRect().bottom - msgs[start].getBoundingClientRect().top <= el.clientHeight - bandH;
      const aim = runFits ? msgs[start] : last;
      const covered = el.getBoundingClientRect().top + bandH - aim.getBoundingClientRect().top;
      if (covered > 1) el.scrollTop -= Math.ceil(covered);
    }
    landedAt.current = el.scrollTop;
  };
  // Keep the view where the reader was: at the bottom after a load or a send, in place after older notes arrive.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (keepFromBottom.current !== null) { el.scrollTop = el.scrollHeight - keepFromBottom.current; keepFromBottom.current = null; landedAt.current = null; return; }
    if (stickToBottom.current) {
      stickToBottom.current = false;
      landNewest(el);
    }
  }, [items, pending]);
  // 2026-10-28: the list's height settles after it lands (the panel slides in, the touches row folds, the
  // box grows with a draft). Until the reader scrolls, the newest note stays in view through every such
  // change, so a case opened on Notes shows its newest note and "Jump to newest" only when scrolled away.
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let h = el.clientHeight;
    const ro = new ResizeObserver(() => {
      if (el.clientHeight === h) return;
      h = el.clientHeight;
      if (landedAt.current !== null) landNewest(el);
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const loadOlder = useCallback(async () => {
    if (!patientId || !cursor || older) return;
    setOlder(true);
    try {
      const r = await advocateApi.residentNotes(patientId, { before: cursor, q, limit: 50 });
      if (r.success && r.data) {
        const el = scroller.current;
        keepFromBottom.current = el ? el.scrollHeight - el.scrollTop : null;
        const have = new Set(items.map((n) => n.id));
        const earlier = [...r.data.items].reverse().filter((n) => !have.has(n.id));
        setItems((prev) => [...earlier, ...prev]);
        setCursor(r.data.next_cursor);
        setSaid(`${earlier.length} earlier note${earlier.length === 1 ? '' : 's'} loaded above.`);
      } else setErr(sendError(r));
    } catch { setErr('Could not reach the server for earlier notes.'); } finally { setOlder(false); }
  }, [patientId, cursor, older, q, items]);

  // Scrolling to the top asks for earlier notes; the button does the same for keyboards and old browsers.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof IntersectionObserver === 'undefined' || !cursor) return undefined;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) loadOlder(); }, { root: scroller.current, rootMargin: '80px' });
    io.observe(el);
    return () => io.disconnect();
  }, [cursor, loadOlder]);

  /** Read the newest page again and merge it in (the server's copy replaces a local one): the thread is the only thing that refreshes. */
  const refreshNewest = useCallback(async (stick = false): Promise<void> => {
    if (!patientId) return;
    const r = await advocateApi.residentNotes(patientId, { q, limit: 50 });
    if (!r.success || !r.data) return;
    setPage((prev) => (prev ? { ...prev, ...r.data!, items: prev.items } : r.data!));
    const fresh = [...r.data.items].reverse();
    const byId = new Map(fresh.map((n) => [n.id, n]));
    // Set in the same tick as the merge, so the render that adds the notes is the one that scrolls.
    if (stick) stickToBottom.current = true;
    setItems((prev) => {
      const have = new Set(prev.map((n) => n.id));
      return [...prev.map((n) => byId.get(n.id) || n), ...fresh.filter((n) => !have.has(n.id))];
    });
  }, [patientId, q]);
  refreshNow.current = () => refreshNewest(true);

  // Another tab of the same person on the same resident confirmed (or took back with Edit) a note this
  // panel shows as "Not confirmed": it left the outbox there, so it leaves here, and the thread re-reads.
  // Fix round 5: a duplicate of this tab wrote THIS tab's box -- its words stay with it, and this
  // tab's words move to a new tab id; a tab that closed its thread left its box at rest, so it comes
  // here as "Draft kept"; and a draft another tab set aside appears here too.
  useEffect(() => {
    if (!patientId || !userId) return undefined;
    const timers = new Set<number>();
    const syncKept = () => {
      const have = new Set(pendingNow.current.map((x) => x.key));
      const fresh = fromStore(readOutbox(su(), patientId)).filter((x) => x.hold === 'kept' && !have.has(x.key));
      if (fresh.length) setPending((list) => [...list, ...fresh.filter((x) => !list.some((y) => y.key === x.key))]);
    };
    const onStorage = (ev: StorageEvent) => {
      try { if (ev.storageArea && ev.storageArea !== window.localStorage) return; } catch { return; }
      // Fix round 8: she signed out in another tab. The marker is written BEFORE her keys are removed, so
      // the removals that follow (heard below as a box taken) find su() null and write nothing back.
      if (isSignOutKeyFor(ev.key, userId)) {
        if (!live()) afterSignOutNow.current();
        return;
      }
      if (!live()) { afterSignOutNow.current(); return; }
      if (isOwnDraftKey(ev.key, userId, patientId, tabRef.current)) {
        const mine = textNow.current;
        // This tab's box was REMOVED (fix round 7): taken by another tab while this one was quiet, or
        // cleared by a duplicate's Send. It is settled here, under this tab's own id -- never copied a
        // second time under a new one. A removal heard late (a frozen tab's queued event, after it has
        // already written its box back on waking) finds the box in place and changes nothing.
        if (ev.newValue === null) {
          if (mine.trim()) reconcileNow.current();
          return;
        }
        const u = su();
        // Fix round 11: the very box this tab last saved, written under another name (the prune renames a key
        // dated in the future to today): that is this tab's own box, not a duplicate tab's.
        let theirs = '';
        try { const v = JSON.parse(ev.newValue || 'null'); const w = v && v.value; theirs = typeof w === 'string' ? w : (w && typeof w.text === 'string' ? w.text : ''); } catch { theirs = ''; }
        if (theirs && theirs === boxSeen.current) return;
        if (mine.trim() && u) {
          tabRef.current = renewTabId();
          markThreadOpen(u, patientId, tabRef.current);
          const at = Date.now();
          const wrote = writeComposer(u, patientId, mine, editOf.current, at, tabRef.current, withCaution(aimNow.current));
          setUnsaved(!wrote, true);
          // Fix round 10: only a write that landed is this tab's box (as in saveBox).
          if (wrote) { boxAt.current = at; boxSeen.current = mine; } else { boxAt.current = 0; boxSeen.current = ''; }
        }
        return;
      }
      if (isPresenceKeyFor(ev.key, userId, patientId)) {
        // Another tab closed its thread. A reload closes and reopens it within a second or two, and its
        // words belong back in its own box: so wait, then take only a box whose thread stayed closed.
        if (ev.newValue === null) {
          const t = window.setTimeout(() => {
            timers.delete(t);
            if (adoptLeftoverDrafts(su(), patientId, tabRef.current, { caseId: actingCaseId }) > 0) syncKept();
          }, 6000);
          timers.add(t);
        }
        return;
      }
      if (!isOwnOutboxKey(ev.key, userId, patientId)) return;
      syncKept();
      const still = new Set(readOutbox(su(), patientId).map((x) => x.key));
      const gone = pendingNow.current.filter((x) => x.stored && x.state === 'failed' && !still.has(x.key)).map((x) => x.key);
      if (!gone.length) return;
      setPending((list) => list.filter((x) => !gone.includes(x.key)));
      refreshNewest().catch(() => { /* the next open reads the server's copy */ });
    };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('storage', onStorage); timers.forEach((t) => window.clearTimeout(t)); };
  }, [userId, patientId, refreshNewest, actingCaseId]);

  const writable = useMemo(() => page?.writable_cases || [], [page]);
  const targetCase = writable.find((w) => w.case_id === target) ? target : (writable[0]?.case_id || actingCaseId);
  aimNow.current = { caseId: targetCase, kind };
  const mayWrite = canWrite && Boolean(page) && !page!.read_only && writable.length > 0;

  /**
   * Fix round 14: a send may settle after the thread closed (another section, Prev/Next, the panel closed), when its
   * own state is gone. Every way it settles leaves each word that is not on the server in storage or in memory:
   *   - refused or failed: the note stays as it was -- in the outbox, or (storage full) in memory, where the thread put
   *     it on its last render (it holds unstored notes whatever their state); a reopened thread shows it "Not sent";
   *   - confirmed: the words are the server's; storage and memory let them go, a reopened thread too;
   *   - "already recorded", the same words: the server's; likewise;
   *   - "already recorded", other words (or unreadable): those words are kept under a new key -- in the outbox, or in
   *     memory in place of the replayed note BEFORE it is let go -- and a reopened thread shows them as that draft.
   */
  async function send(p: Pending) {
    setPending((list) => list.map((x) => (x.key === p.key ? { ...x, state: 'sending', error: undefined } : x)));
    setSaid('Sending your note.');
    let r: Awaited<ReturnType<typeof advocateApi.addNote>>;
    try {
      r = await advocateApi.addNote(p.caseId, { kind: p.kind, note: p.note, idempotency_key: p.key });
    } catch {
      r = { success: false } as typeof r;
    }
    if (!r.success || !r.data) {
      const error = sendError(r);
      setPending((list) => list.map((x) => (x.key === p.key ? { ...x, state: 'failed', error } : x)));
      setSaid(`Your note was not sent. ${error}`);
      return;
    }
    // Fix round 8: this was the "Draft kept" copy of another tab's box, and its key has recorded a note
    // (now, or on an earlier send). The tab it came from may wake still holding the words: a text-free
    // mark tells it they were sent, so it never writes them back to be sent a second time.
    leaveSentMark(su(), patientId, p.key, String(r.data.noteId));
    // A replay: this key had ALREADY recorded a note, and the answer names that note, not these words.
    // Fix round 6: EVERY replay is checked against the server's own copy -- its words, its kind and its
    // case -- whatever this tab remembers about the note (another tab may have sent it, or an earlier
    // version of it, at any time). Only an exact match is "Note sent."; any difference, or a copy that
    // cannot be read back, keeps these words as "Draft kept" with the reason. They are kept FIRST, under
    // a key of their own, so there is no moment they are kept nowhere.
    if (r.data.replayed) {
      const noteId = r.data.noteId;
      const keptKey = freshKey();
      const keptNote: StoredNote = { key: keptKey, caseId: p.caseId, kind: p.kind === 'VETTING' ? 'VETTING' : 'GENERAL', note: p.note, hold: 'kept', why: 'earlier' };
      addToOutbox(su(), patientId, keptNote);
      const keptStored = readOutboxNote(su(), patientId, keptKey) !== null;
      // Fix round 14: these words are not the server's. Kept in storage, or -- storage full -- in this page's memory
      // in place of the replayed note, BEFORE the replayed note is let go: this send may settle after the thread
      // closed, when nothing else would ever hold them (the thread's own state is gone with it).
      if (!keptStored && su()) replaceMemoryNote(userId, patientId, tabRef.current, p.key, keptNote);
      dropConfirmed(userId, patientId, p.key);
      if (keptStored || !su()) forgetMemoryNote(userId, patientId, p.key);
      const kept: Pending = {
        key: keptKey, caseId: p.caseId, kind: p.kind, note: p.note, state: 'failed', at: new Date().toISOString(),
        hold: 'kept', why: 'earlier', stored: keptStored,
      };
      // Fix round 7: the recorded note may be older than the newest 50 (a lost answer retried days later),
      // so the thread is read back page by page, newest first, until it is found or the thread ends --
      // at most REPLAY_PAGES pages; only then is it "could not be read back".
      let server: ResidentNote | undefined;
      try {
        let before: string | null = null;
        for (let i = 0; i < REPLAY_PAGES && !server; i += 1) {
          const chk = await advocateApi.residentNotes(patientId!, before ? { before, limit: 50 } : { limit: 50 });
          if (!chk.success || !chk.data) break;
          server = chk.data.items.find((n) => n.id === noteId);
          before = chk.data.next_cursor;
          if (!before) break;
        }
      } catch { /* unreachable: what was found so far stands; nothing found is "could not be read back" */ }
      stickToBottom.current = true;
      const differs = server ? replayDiffers(server, p) : null;
      if (server && differs && differs.length === 0) {
        // The server's copy under this key IS this note (an earlier send of this very version landed).
        removeFromOutbox(userId, patientId, keptKey);
        // Fix round 14: so nothing of it stays in memory either, and a reopened thread lets it go.
        forgetMemoryNote(userId, patientId, keptKey);
        announceConfirmed(userId, patientId, p.key);
        mineLocal.current.add(server.id);
        setItems((prev) => (prev.some((n) => n.id === server!.id) ? prev : [...prev, server!]));
        setPending((list) => list.filter((x) => x.key !== p.key));
        setSaid('Note sent.');
        onNoteAdded?.(p.caseId, { kind: p.kind, preview: p.note.slice(0, 240), recorded_at: server.recorded_at });
      } else {
        // Fix round 14: a thread reopened since, showing the replayed note as "Not sent", shows these words as the
        // kept draft instead (its new key): a Send there is a new note, never the replayed one again.
        announceConfirmed(userId, patientId, p.key, keptNote);
        setPending((list) => [...list.filter((x) => x.key !== p.key), kept]);
        setSaid(server && differs
          ? `This note had already been recorded from an earlier send, ${differs.join(', ')}. This version was not recorded; it is kept below as a draft.`
          : 'This note had already been recorded from an earlier send, and that version could not be read back to compare. Your words are kept below as a draft: compare them with the thread, then send them as a new note or discard them.');
      }
      // The re-read brings the recorded version into the thread ABOVE any kept words: it keeps the
      // view at the bottom, so the kept words and their reason stay in sight.
      try { await refreshNewest(true); } catch { /* the next open reads the server's copy */ }
      return;
    }
    // Recorded. From here nothing may say otherwise: the note joins the thread
    // now, from what was sent, whether or not the re-read below succeeds or a
    // search filter would hide it.
    const ep = writable.find((w) => w.case_id === p.caseId);
    const confirmed: ResidentNote = {
      id: r.data.noteId, source: 'PA', kind: p.kind, note: p.note,
      recorded_at: r.data.recordedAt ? new Date(r.data.recordedAt).toISOString() : new Date().toISOString(),
      author: { id: userId, name: 'You', role: null }, case_id: p.caseId,
      episode: { case_id: p.caseId, label: ep ? ep.label : 'This case', open: true }, provenance: 'EMERGE',
    };
    // Confirmed: nothing of it stays in this browser, even if this panel has already closed.
    dropConfirmed(userId, patientId, p.key);
    // Fix round 12: nor in this page's memory (a note on screen only, confirmed after the thread closed).
    forgetMemoryNote(userId, patientId, p.key);
    // Fix round 13: and a thread reopened since, showing this note as "Not sent", lets it go.
    announceConfirmed(userId, patientId, p.key);
    mineLocal.current.add(confirmed.id);
    stickToBottom.current = true;
    setItems((prev) => (prev.some((n) => n.id === confirmed.id) ? prev : [...prev, confirmed]));
    setPending((list) => list.filter((x) => x.key !== p.key));
    setPage((pg) => (pg ? { ...pg, total: pg.total + 1 } : pg));
    setSaid('Note sent.');
    onNoteAdded?.(p.caseId, { kind: p.kind, preview: p.note.slice(0, 240), recorded_at: confirmed.recorded_at });
    try { await refreshNewest(); } catch { /* the note is on screen; the next open reads the server's copy */ }
  }

  /** `words`: send these instead of the box's (fix round 11: "Send only the new part"). */
  function submit(words?: string) {
    const note = (words !== undefined ? words : text).trim();
    if (!note || !patientId) return;
    // The key is made when the note is composed and kept for every retry: a retry is never a second note.
    // A note taken back with Edit is the SAME note, changed or not (fix round 5): it keeps its key, so if
    // its first send did land the server answers with that one instead of recording a second. Whether
    // the answer is these words is never taken from this tab's memory (fix round 6: another tab may have
    // sent the note, or a kept draft, since this tab brought it into the box): send() checks every
    // replay against the server's copy.
    const e = editOf.current;
    editOf.current = null;
    const p: Pending = { key: e ? e.key : freshKey(), caseId: targetCase, kind, note, state: 'sending', at: new Date().toISOString() };
    // The words move from the draft to the outbox in one step, so there is no moment they are kept nowhere.
    // An edited note's "Editing" entry becomes the sending one (same key).
    addToOutbox(su(), patientId, { key: p.key, caseId: p.caseId, kind: p.kind, note: p.note });
    // Fix round 11: a note that could not be written here (a full quota) is marked "Not saved on this computer"
    // on screen, the notice beside the box stays up for it, and leaving the page warns first. The box empties as
    // usual -- one copy of the words on screen, under one key, so a Retry can never record them twice.
    const kept = su() ? readOutboxNote(su(), patientId, p.key) : null;
    p.stored = Boolean(kept && kept.note === p.note);
    setPending((list) => [...list.filter((x) => x.key !== p.key), p]);
    if (!p.stored && su()) setSaid(UNSAVED_ITEMS);
    setText('');
    saveBox('', null);
    setBoxNote(null);
    setCaution(false);
    setSameAsk(null);
    stickToBottom.current = true;
    send(p);
  }

  /**
   * Fix round 8: the newest notes, read from the server, for a note of hers with exactly these words; and
   * this browser's unsent notes for the same words. Null: nothing the same. 'unknown': the thread could not
   * be read, so it cannot be told.
   */
  async function findSameNote(words: string, since: number, exceptKey: string | null = null, partial = false): Promise<SameNote | null> {
    const waiting = readOutbox(su(), patientId).find((p) => p.note.trim() === words && p.key !== exceptKey && !(editOf.current && editOf.current.key === p.key));
    if (waiting) return { where: 'unsent', key: waiting.key };
    // Fix round 9: not only the newest 50. Page back, as a replay's read-back does, until the notes are older
    // than a day before these words were last saved (a copy cannot be older; the day covers clock skew), or
    // the thread ends. Past REPLAY_PAGES pages with the thread still going, it cannot be told: it asks.
    const floor = since > 1 ? since - DAY_MS : 0;
    // Fix round 11: under a PARTIAL caution (fix round 12: only then), a note of hers whose words BEGIN these --
    // the words a box last saved, sent from another tab, then typed on -- is a hit too, with its own question.
    // Fix round 12: recorded no earlier than PART_SLACK_MS before the caution, at least PART_MIN_CHARS long, at
    // a word boundary. The exact match wins wherever it is found; the newest such note is kept until the paging ends.
    const partFloor = since > 1 ? since - PART_SLACK_MS : 0;
    let part: SameNote | null = null;
    let partNote: ResidentNote | null = null;
    const show = (hit: ResidentNote) => setItems((prev) => (prev.some((n) => n.id === hit.id) ? prev : [...prev, hit].sort((a, b) => a.recorded_at.localeCompare(b.recorded_at))));
    /**
     * The paging ended without an exact match: the leading note, if any, is the answer (and is shown). Fix round
     * 12: under a partial caution with none found, the PA is asked ('partMissing'), never sent to in silence.
     */
    const settle = (): SameNote | null => { if (partNote) { show(partNote); return part; } return partial ? { where: 'partMissing' } : null; };
    try {
      let before: string | null = null;
      for (let i = 0; i < REPLAY_PAGES; i += 1) {
        const r = await advocateApi.residentNotes(patientId!, before ? { before, limit: SAME_NOTE_WINDOW } : { limit: SAME_NOTE_WINDOW });
        if (!r.success || !r.data) return { where: 'unknown' };
        // The header's count is the server's (fix round 9): a note found here and added below is counted there too.
        if (i === 0 && !q) { const total = r.data.total; setPage((pg) => (pg ? { ...pg, total } : pg)); }
        const hers = (n: ResidentNote) => n.source === 'PA' && (n.author.id ? Boolean(userId) && n.author.id === userId : mineLocal.current.has(n.id));
        const hit = r.data.items.find((n) => hers(n) && String(n.note || '').trim() === words);
        if (hit) {
          show(hit);
          return { where: 'thread', noteId: hit.id, at: hit.recorded_at };
        }
        if (partial && !part) {
          const lead = r.data.items.find((n) => hers(n) && isLeadingPart(String(n.note || '').trim(), words) && (!partFloor || Date.parse(n.recorded_at) >= partFloor));
          if (lead) {
            const t = String(lead.note || '').trim();
            part = { where: 'part', noteId: lead.id, at: lead.recorded_at, text: t, rest: words.slice(t.length).trim() };
            partNote = lead;
          }
        }
        before = r.data.next_cursor;
        if (!before) return settle();
        const oldest = r.data.items[r.data.items.length - 1];
        if (floor && oldest && Date.parse(oldest.recorded_at) < floor) return settle();
      }
      // Past REPLAY_PAGES pages: a leading note found is the answer; nothing found cannot be told.
      if (partNote) return settle();
      return { where: 'unknown' };
    } catch {
      return { where: 'unknown' };
    }
  }

  /**
   * Send, from the box. Fix round 8: a box this tab wrote back after another tab took it (and nothing said
   * what became of the words there) carries a caution, and its Send first looks for these very words in
   * the thread and in the unsent notes: found, or not checkable, it asks before sending them again. A box
   * taken while this tab slept and never heard of is settled first, the same way a wake settles it.
   */
  async function trySend(force = false): Promise<void> {
    const words = text.trim();
    if (!words || !patientId || checking) return;
    const u = su();
    if (u && boxSeen.current.trim() && !readComposer(u, patientId, Date.now(), tabRef.current).text) {
      const woke = reconcileBox();
      // 'partial': part of the box went from another tab; the PA reads why before anything is sent.
      if (woke === 'sent' || woke === 'elsewhere' || woke === 'partial') return;
    }
    // Fix round 10: a cautioned box asks first EVEN while it holds an edited note. The edited note's own key
    // never records it twice, but the copy another tab may have sent went under ANOTHER key (the kept copy's).
    if (cautionNow.current && !force) {
      setChecking(true);
      setSameAsk(null);
      const same = await findSameNote(words, cautionNow.current, null, partialNow.current);
      setChecking(false);
      if (textNow.current.trim() !== words) return; // the words changed while it looked: the PA sends again
      if (same) {
        setSameAsk(same);
        setSaid(sameWords(same));
        return;
      }
    }
    submit();
  }
  const sameWords = (s: SameNote, fromBox = true): string => (s.where === 'thread'
    ? `The thread already has a note of yours with exactly these words, from ${relativeTime(s.at)}. Send them again?`
    : s.where === 'part'
      // Fix round 11: part of these words is already recorded (a box taken and sent as saved, then typed on).
      // Fix round 12: the question quotes that note, so the PA sees which words "the new part" leaves out.
      ? `These words begin with a note of yours from ${relativeTime(s.at)}: “${quoteOf(s.text)}” Send only the new part, send all, or don’t send?`
    : s.where === 'partMissing'
      ? PART_MISSING
    : s.where === 'unsent'
      ? (fromBox ? 'Your unsent notes already hold exactly these words. Send them again from the box?' : 'Another of your unsent notes holds exactly these words. Send this one as well?')
      : 'The thread could not be read to check whether these words were already sent. Send them anyway?');

  /**
   * Send (a kept draft) or Retry (an unsent note) from the list. Fix round 10: a note that carries a caution
   * -- its words came from a box written back under one -- first looks for the very same words in the thread
   * and in the other unsent notes, exactly as the box's Send does, and asks on the note itself before sending
   * them again. Once checked (or "Send anyway"), the caution has done its work and a later Retry is plain.
   */
  async function sendItem(p: Pending, how: 'kept' | 'retry', force = false): Promise<void> {
    const cur = pendingNow.current.find((x) => x.key === p.key) || p;
    if (cur.caution && !force) {
      if (checkingKeyNow.current) return;
      checkingKeyNow.current = p.key;
      setCheckingKey(p.key);
      setItemAsk(null);
      const same = await findSameNote(cur.note.trim(), cur.caution, cur.key, Boolean(cur.partial));
      checkingKeyNow.current = null;
      setCheckingKey(null);
      if (same) {
        setItemAsk({ key: p.key, same });
        setSaid(sameWords(same, false));
        return;
      }
    }
    const latest = pendingNow.current.find((x) => x.key === p.key);
    if (!latest) return; // discarded, or confirmed by another tab, while it looked
    if (latest.caution) uncautionItem(p.key);
    const plain: Pending = { ...latest, caution: undefined, partial: undefined };
    if (how === 'kept') sendKept(plain); else send(plain);
  }

  /**
   * Fix round 11, "Send only the new part" on a note: its words become what follows the part already recorded
   * (under the same key, in storage first), and that is sent -- the recorded part is never sent again.
   */
  function sendItemRest(p: Pending, how: 'kept' | 'retry', rest: string): void {
    const latest = pendingNow.current.find((x) => x.key === p.key);
    if (!latest || !rest.trim()) return;
    const next: Pending = { ...latest, note: rest.trim(), caution: undefined, partial: undefined };
    addToOutbox(su(), patientId, {
      key: next.key, caseId: next.caseId, kind: next.kind === 'VETTING' ? 'VETTING' : 'GENERAL', note: next.note,
      ...(next.hold ? { hold: next.hold } : {}), ...(next.hold && next.why ? { why: next.why } : {}),
    });
    const kept = su() ? readOutboxNote(su(), patientId, next.key) : null;
    next.stored = Boolean(kept && kept.note === next.note);
    setPending((list) => list.map((x) => (x.key === p.key ? next : x)));
    pendingNow.current = pendingNow.current.map((x) => (x.key === p.key ? next : x));
    if (how === 'kept') sendKept(next); else send(next);
  }

  /** Bring one note of the thread into view and focus it (it is read again from the server if not loaded). */
  async function showNote(noteId: string): Promise<void> {
    const find = (): HTMLElement | null => (Array.from(scroller.current?.querySelectorAll('[data-note-id]') || []) as HTMLElement[])
      .find((el) => el.getAttribute('data-note-id') === noteId) || null;
    if (!find()) {
      if (q) { setQ(null); setQDraft(''); }
      try { await refreshNewest(); } catch { /* shown if it can be */ }
    }
    // Focused once the render that marks it (tabIndex -1) is on screen: the effect below.
    setFoundId({ id: noteId, n: Date.now() });
  }
  useEffect(() => {
    if (!foundId) return;
    const el = (Array.from(scroller.current?.querySelectorAll('[data-note-id]') || []) as HTMLElement[]).find((x) => x.getAttribute('data-note-id') === foundId.id);
    if (!el) { setSaid('That note is not among the newest notes. Load earlier notes to find it.'); return; }
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
    el.focus();
  }, [foundId]);

  // The question a cautioned note's Send asks is brought into view on that note: the same words found in the
  // thread are added above it, which would otherwise push it down out of sight.
  useEffect(() => {
    if (!itemAsk) return;
    const el = scroller.current?.querySelector('[data-item-ask]') as HTMLElement | null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [itemAsk]);

  /** Set a draft aside as its own unsent item ("Draft kept"): never sent, never overwritten. */
  function keepAside(words: string, k: NoteKind, caseId: string, caution = 0, partial = false): void {
    // Fix round 10: words set aside from a cautioned box keep its caution, so their Send asks first too.
    // Fix round 11: and whether part of them already went from another tab.
    const c = caution > 0 ? { caution, ...(partial ? { partial: true } : {}) } : {};
    const kept: Pending = { key: freshKey(), caseId, kind: k, note: words, state: 'failed', at: new Date().toISOString(), hold: 'kept', ...c };
    addToOutbox(su(), patientId, { key: kept.key, caseId, kind: k, note: words, hold: 'kept', ...c });
    kept.stored = readOutboxNote(su(), patientId, kept.key) !== null;
    setPending((list) => [...list, kept]);
  }

  /** The editing note goes back to what it was (unsent, or a kept draft). Its words were never out of the list. */
  function stopEditingOf(key: string): void {
    const was = pendingNow.current.find((x) => x.key === key);
    if (!was || was.hold !== 'editing') return;
    setOutboxHold(su(), patientId, key, was.from === 'kept' ? 'kept' : null);
    setPending((list) => list.map((x) => (x.key === key ? settle(x) : x)));
  }

  /**
   * Edit: bring an unsent note (or a kept draft) into the box. Whatever the box holds that is not
   * already safe elsewhere is set aside first, never overwritten; the note stays in the list as
   * "Editing" with its original key until it is sent or discarded.
   */
  function editPending(p: Pending): void {
    const cur = editOf.current;
    if (cur && cur.key === p.key) { composer.current?.focus(); return; }
    const typed = text.trim();
    // Fix round 10: the caution is about the words in the box. Wherever those words go, it goes with them.
    const cautioned = typed ? cautionNow.current : 0;
    const partialWas = Boolean(cautioned && partialNow.current);
    if (cur) {
      // The box holds another note being edited: that note goes back to the list as it was, and
      // any words the PA changed in the box are kept as a draft of their own.
      stopEditingOf(cur.key);
      if (typed && typed !== cur.note) keepAside(typed, kind, targetCase, cautioned, partialWas);
      else if (cautioned) cautionItem(cur.key, cautioned, partialWas); // the cautioned words are that note's own, back in the list
    } else if (typed && typed !== p.note) {
      keepAside(typed, kind, targetCase, cautioned, partialWas);
    }
    // The box now holds THIS note: a caution stays only if these are the cautioned words, or the note carries
    // one of its own. Otherwise the banner went with the words it was about (it used to stay above another note).
    const sameWordsHere = Boolean(typed && typed === p.note.trim());
    const nextCaution = [sameWordsHere ? cautioned : 0, p.caution || 0].filter((t) => t > 0);
    setCaution(false);
    if (nextCaution.length) setCaution(true, Math.min(...nextCaution), (sameWordsHere && partialWas) || Boolean(p.caution && p.partial));
    setSameAsk(null);
    const edit: DraftEdit = { key: p.key, caseId: p.caseId, kind: p.kind, note: p.note };
    const from = p.hold === 'kept' ? 'kept' : undefined;
    setOutboxHold(su(), patientId, p.key, 'editing', from || null);
    setPending((list) => list.map((x) => (x.key === p.key ? { ...x, hold: 'editing', from, state: 'failed' } : x)));
    editOf.current = edit;
    setText(p.note); setKind(p.kind); setTarget(p.caseId);
    saveBox(p.note, edit, { caseId: p.caseId, kind: p.kind });
    setSaid('The note is in the box below to edit. It stays in your unsent notes until you send or discard it.');
    window.requestAnimationFrame(() => composer.current?.focus());
  }

  /** Stop editing: the note goes back to the list; words the PA changed in the box stay there as a plain draft. */
  function stopEditing(p: Pending): void {
    stopEditingOf(p.key);
    if (editOf.current && editOf.current.key === p.key) {
      editOf.current = null;
      const typed = text.trim();
      if (!typed || typed === p.note) {
        // Fix round 10: the box's words were the note's own: they are in the list, and so is the caution.
        if (typed && cautionNow.current) cautionItem(p.key, cautionNow.current, partialNow.current);
        setCaution(false); setSameAsk(null);
        setText(''); saveBox('', null);
      } else saveBox(text, null, { caseId: targetCase, kind });
    }
    setSaid('Stopped editing. The note is back in your unsent notes.');
  }

  /** Discard: the one way an unsent note's words leave this browser without being sent. Always the PA's own click. */
  function discard(p: Pending): void {
    removeFromOutbox(userId, patientId, p.key);
    setPending((list) => list.filter((x) => x.key !== p.key));
    setBoxNote(null);
    if (editOf.current && editOf.current.key === p.key) {
      editOf.current = null;
      // The box held this note's words: unchanged, they go with it; changed, they stay as a plain draft.
      const typed = text.trim();
      if (!typed || typed === p.note) { setCaution(false); setSameAsk(null); setText(''); saveBox('', null); } else saveBox(text, null, { caseId: targetCase, kind });
    }
    setSaid('Unsent note discarded.');
  }

  /**
   * Typing. Emptying the box while it holds a note being edited lets that note go back to the list as
   * it was (fix round 5): a later Send is then a new note, never one that silently replaces it.
   */
  function onType(value: string): void {
    setText(value);
    // The question was about the words as they were; an emptied box has nothing left to be cautious about.
    if (sameAsk) setSameAsk(null);
    if (!value.trim()) setCaution(false);
    if (editOf.current && !value.trim()) {
      stopEditingOf(editOf.current.key);
      editOf.current = null;
    }
    saveBox(value, editOf.current, { caseId: targetCase, kind });
  }

  /** The kind or the case changed: the box's words are kept with where they are aimed. */
  function aimAt(caseId: string, k: NoteKind): void {
    setTarget(caseId); setKind(k);
    if (text.trim()) saveBox(text, editOf.current, { caseId, kind: k });
  }

  /**
   * Discard the words in a box this reader cannot write from right now: her own click. While the box
   * holds a note being edited, that note goes back to the list as it was (the changed words were the
   * box's; the note's own words were never out of the list).
   */
  function discardBox(): void {
    const e = editOf.current;
    if (e) { stopEditingOf(e.key); editOf.current = null; }
    setText('');
    saveBox('', null);
    setCaution(false);
    setSameAsk(null);
    setSaid('The words from your note box were discarded.');
  }

  // Fix round 6: the box is hidden while this reader may not write HERE (the panel moved to a former
  // case of the resident, a role without add_note), but a note being edited stays the box's: the
  // association is kept, and when the panel comes back to a case she can write on, Send goes with the
  // note's own key -- never as a second note. Only the server saying the whole thread is read-only to
  // her (she no longer holds the resident), Stop editing or Discard ends it. Not one-shot: every time
  // the server's page says read-only.
  const pageReadOnly = Boolean(page && page.read_only);
  useEffect(() => {
    if (!pageReadOnly) return;
    const e = editOf.current;
    if (!e) return;
    stopEditingOf(e.key);
    editOf.current = null;
    const typed = textNow.current.trim();
    if (!typed || typed === e.note.trim()) {
      // Fix round 10: the cautioned words are the note's own, back in the list: the caution goes with them.
      if (typed && cautionNow.current) cautionItem(e.key, cautionNow.current, partialNow.current);
      setCaution(false);
      setText(''); saveBox('', null);
    } else saveBox(textNow.current, null, { caseId: e.caseId, kind: e.kind });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageReadOnly]);
  // The box's words, shown while the box is hidden -- unless they are exactly the note being edited,
  // which the list already shows as "Editing".
  const heldEdit = editOf.current;
  const boxShownReadOnly = Boolean(page) && !mayWrite && Boolean(text.trim()) && !(heldEdit && heldEdit.note.trim() === text.trim());

  /** Send a kept draft as it is, with its own key. */
  function sendKept(p: Pending): void {
    setOutboxHold(su(), patientId, p.key, null);
    const next: Pending = { ...p, hold: undefined, from: undefined };
    setPending((list) => list.map((x) => (x.key === p.key ? next : x)));
    send(next);
  }

  // Fix round 11: words that live on this screen only -- the box while its writes fail, or an unsent note that
  // could not be stored (a full quota) -- are said beside the box and on the note, and leaving or reloading the
  // page asks first. Without a signed-in user, or after her sign-out, nothing is stored by design: no warning.
  const storing = Boolean(userId) && !signedOut;
  const unstoredItems = storing && pending.some((p) => p.stored === false);
  // Fix round 13: the box is on screen only while its words differ from what storage holds for it -- not merely
  // while the "couldn't be saved" notice settles (a write that landed used to keep the box held for 1.5 s, and a
  // thread closed in that window brought back words storage had held and another tab could take and send).
  const boxOnScreenOnly = storing && unsaved && Boolean(text.trim()) && text !== boxSeen.current;
  const memOnly = storing && (boxOnScreenOnly || unstoredItems);
  useEffect(() => {
    if (!memOnly) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = LEAVE_WARNING; return LEAVE_WARNING; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [memOnly]);

  /**
   * Fix round 12: what is on screen only is also held in this page's memory (noteStorage.holdInMemory), in step
   * with every render, so the thread closing in-app (another section, Prev/Next, the panel closed) never loses it:
   * it comes back when the thread opens again. Stored, sent or discarded, it leaves; after her sign-out, all goes.
   */
  const heldTab = useRef<string | null>(null);
  useEffect(() => {
    if (!userId || !patientId) return;
    const tab = tabRef.current;
    // This tab took a new id (a duplicate tab): what it held under the old one moves with it.
    if (heldTab.current && heldTab.current !== tab) holdInMemory(userId, patientId, heldTab.current, null, []);
    heldTab.current = tab;
    if (!storing) { forgetInMemory(userId); return; }
    // Fix round 13: held only while storage does not hold these words, with what storage DID hold (and when), so a
    // reopened thread runs the taken-box check against it.
    const box: HeldBox | null = boxOnScreenOnly
      ? {
        text, edit: editOf.current, caseId: targetCase, kind: kind === 'VETTING' ? 'VETTING' : 'GENERAL',
        ...(cautionNow.current ? { caution: cautionNow.current, ...(partialNow.current ? { partial: true } : {}) } : {}),
        stored: boxSeen.current, storedAt: boxSeen.current ? boxAt.current : 0,
      }
      : null;
    const onScreenOnly: StoredNote[] = pending.filter((p) => p.stored === false).map((p) => ({
      key: p.key, caseId: p.caseId, kind: p.kind === 'VETTING' ? 'VETTING' : 'GENERAL', note: p.note,
      ...(p.hold ? { hold: p.hold } : {}), ...(p.hold === 'editing' && p.from ? { from: p.from } : {}), ...(p.hold && p.why ? { why: p.why } : {}),
      ...(p.caution ? { caution: p.caution, ...(p.partial ? { partial: true } : {}) } : {}),
    }));
    holdInMemory(userId, patientId, tab, box, onScreenOnly);
  });
  // Fix round 13: a note this thread shows as unsent (brought back from memory) whose first send -- from the thread
  // that closed -- was confirmed after this one opened: it leaves the list, and the thread shows the recorded note.
  useEffect(() => {
    if (!userId || !patientId) return undefined;
    return onNoteConfirmed((u, p, key, replacement) => {
      if (u !== userId || p !== patientId) return;
      if (!pendingNow.current.some((x) => x.key === key && x.state === 'failed')) return;
      if (replacement) {
        // Fix round 14: the replayed note is the server's; its other words wait here as a kept draft (new key).
        const next: Pending = { ...fromStore([replacement])[0], stored: readOutboxNote(su(), patientId, replacement.key) !== null };
        stickToBottom.current = true;
        setPending((list) => [...list.filter((x) => x.key !== key && x.key !== replacement.key), next]);
        setSaid('This note had already been recorded from an earlier send, with other words. This version was not recorded; it is kept below as a draft.');
      } else setPending((list) => list.filter((x) => x.key !== key));
      refreshNewest(true).catch(() => { /* the next open reads the server's copy */ });
    });
  }, [userId, patientId, refreshNewest]);
  // Fix round 12: words brought back from memory are tried in storage once at once (there may be room again).
  // Fix round 13: with the box storage held for them restored (boxSeen/boxAt), this first write runs the taken-box
  // check (saveBox -> reconcileBox) when that box is gone: sent from another tab, the box comes back under a
  // partial caution; kept there as a draft, both are kept and said; gone without a word, under a caution.
  const triedBack = useRef(false);
  useEffect(() => {
    if (triedBack.current) return;
    triedBack.current = true;
    if (opened.fromMemory && textNow.current.trim()) saveBox(textNow.current, editOf.current, { caseId: aimNow.current.caseId, kind: aimNow.current.kind });
    else storeUnstored();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Fix round 11, "Send only the new part" from the box: what follows the part already recorded is sent. */
  function sendBoxRest(rest: string): void {
    if (!rest.trim()) return;
    setSameAsk(null);
    submit(rest);
  }

  // Scrolled up into the history: offer the way back to the newest note.
  const onScroll = () => {
    const el = scroller.current;
    if (el && landedAt.current !== null && Math.abs(el.scrollTop - landedAt.current) > 2 && !atBottom()) landedAt.current = null;
    setUnseen(atBottom() ? 0 : 1);
  };

  if (!patientId) {
    return <p className="pa-thread-empty">This case is not linked to a resident, so there is no resident thread. Its notes are under History.</p>;
  }

  // What is on screen: the loaded notes, minus the sources the reader switched off.
  const shown = items.filter((n) => !hidden.has(n.source));
  const counts = items.reduce<Record<string, number>>((acc, n) => { acc[n.source] = (acc[n.source] || 0) + 1; return acc; }, {});
  const monday = page?.monday_history || [];
  const sourcesPresent = (['PA', 'UMED', 'FINANCE'] as NoteSource[]).filter((s) => counts[s]);
  const toggle = (s: string) => setHidden((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; });

  // Group by day; a thin divider wherever the desk or the case changes; one
  // author's notes on one strand on one day read as one message.
  const blocks: Array<{ day: string; notes: ResidentNote[] }> = [];
  for (const n of shown) {
    const d = dayKey(n.recorded_at);
    if (!blocks.length || blocks[blocks.length - 1].day !== d) blocks.push({ day: d, notes: [] });
    blocks[blocks.length - 1].notes.push(n);
  }
  const continues = (prev: ResidentNote | undefined, n: ResidentNote) => Boolean(prev && prev.author.id && prev.author.id === n.author.id
    && strandOf(prev) === strandOf(n));
  // The per-note action's roving index: the last note unless the reader moved it.
  const actIndex = actAt >= 0 && actAt < shown.length ? actAt : shown.length - 1;
  const moveAct = (to: number) => {
    const i = Math.max(0, Math.min(shown.length - 1, to));
    setActAt(i);
    window.requestAnimationFrame(() => actButtons.current[i]?.focus());
  };
  let lastStrand = '';
  let flat = -1;

  return (
    <section className="pa-thread" aria-label="Notes for this resident">
      {/*
        One row: what this is and how many, the living key (each source a
        toggle), and Search. The search field opens in place below it, so the
        conversation keeps the height on a laptop screen; "Write a note" is a
        skip link that appears when it has focus.
      */}
      <header className="pa-thread-head">
        <div className="pa-thread-title">
          <h3>Notes</h3>
          <span className="pa-thread-count">
            {page ? <>{`${page.total} note${page.total === 1 ? '' : 's'}`}{q ? ` matching “${q}”` : <span className="pa-thread-span"> on every case and episode</span>}</> : 'Loading…'}
          </span>
          {(sourcesPresent.length > 1 || monday.length > 0) && (
            <div className="pa-thread-legend" role="group" aria-label="Show notes from"
              // A chip reached by Tab comes WHOLLY into view: Chrome leaves a half-visible one where it is,
              // under the key's right-edge fade (fix round 4). scroll-padding keeps it clear of the fade.
              onFocus={(e) => { const t = e.target as HTMLElement; if (t.classList.contains('pa-thread-src') && typeof t.scrollIntoView === 'function') t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }}>
              {sourcesPresent.map((src) => (
                <button key={src} type="button" className="pa-thread-src" aria-pressed={!hidden.has(src)} onClick={() => toggle(src)} title={SOURCE[src].what}>
                  <Marker shape={SOURCE[src].marker} color={SOURCE[src].color} size={10} />
                  <span>{SOURCE[src].label}</span><span className="n">{counts[src]}</span>
                </button>
              ))}
              {monday.length > 0 && (
                <button type="button" className="pa-thread-src" aria-pressed={!hidden.has('MONDAY')} onClick={() => toggle('MONDAY')} title={SOURCE.MONDAY.what}>
                  <Marker shape={SOURCE.MONDAY.marker} color={SOURCE.MONDAY.color} size={10} />
                  <span>{SOURCE.MONDAY.label}</span><span className="n">{monday.length}</span>
                </button>
              )}
            </div>
          )}
          <button type="button" className="pa-thread-searchbtn" aria-expanded={searchShown} aria-controls="pa-thread-search"
            onClick={() => {
              if (searchShown && !q) { setSearchOpen(false); return; }
              setSearchOpen(true);
              window.requestAnimationFrame(() => searchInput.current?.focus());
            }}>
            <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14"><circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M10.4 10.4 14 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
            <span>Search</span>
          </button>
          {mayWrite && <a className="pa-thread-skip" href="#pa-composer-text" onClick={(e) => { e.preventDefault(); composer.current?.focus(); }}>Write a note</a>}
        </div>
        {searchShown && (
          <form id="pa-thread-search" className="pa-thread-search" role="search" aria-label="Notes search" onSubmit={(e) => { e.preventDefault(); setQ(qDraft.trim() || null); }}>
            <input ref={searchInput} type="search" value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Search these notes" aria-label="Search these notes"
              onKeyDown={(e) => { if (e.key === 'Escape' && !qDraft && !q) { e.preventDefault(); setSearchOpen(false); } }} />
            {q && <button type="button" className="pa-thread-chip" onClick={() => { setQDraft(''); setQ(null); }}>Clear</button>}
          </form>
        )}
      </header>

      <p className="pa-sr-only" role="status" aria-live="polite">{said}</p>

      <div className="pa-thread-scroll" ref={scroller} onScroll={onScroll}>
        {loading && !page && <p className="pa-thread-empty">Loading the notes…</p>}
        {page && (!page.sources.umed || !page.sources.finance) && (
          <p className="pa-thread-scope">{!page.sources.umed && !page.sources.finance ? 'UMED and Finance notes are not shown to your role.' : !page.sources.umed ? 'UMED notes are not shown to your role.' : 'Finance notes are not shown to your role.'}</p>
        )}
        {err && <p className="pa-notice stop" role="alert">{err} <button type="button" className="pa-link" onClick={() => load(q)}>Try again</button></p>}
        {cursor && (
          <div ref={sentinel} className="pa-thread-older">
            <button type="button" className="pa-thread-chip" onClick={loadOlder} disabled={older}>{older ? 'Loading earlier notes…' : 'Load earlier notes'}</button>
          </div>
        )}
        {!cursor && !q && monday.length > 0 && !hidden.has('MONDAY') && (
          <div className="pa-thread-history" role="group" aria-label="From Monday, history">
            {monday.map((h) => (
              <article key={h.case_id} className="pa-thread-monday">
                <header>
                  <Marker shape={SOURCE.MONDAY.marker} color={SOURCE.MONDAY.color} size={10} />
                  <strong>From Monday</strong>
                  <span className="pa-thread-ep">{h.label}</span>
                  <span className="pa-thread-when">date not recorded</span>
                </header>
                {h.notes && <p><span className="lbl">Notes</span>{h.notes}</p>}
                {h.vetting_notes && <p><span className="lbl">Vetting notes</span>{h.vetting_notes}</p>}
              </article>
            ))}
          </div>
        )}
        {page && !loading && shown.length === 0 && pending.length === 0 && !boxShownReadOnly && (
          <p className="pa-thread-empty">{q ? 'No note matches that search.' : items.length ? 'Every source is switched off above.' : 'No notes yet. The first one starts the thread.'}</p>
        )}
        {(page || pending.length > 0) && (
          <ol className="pa-thread-list" aria-label="Notes, oldest first" aria-busy={older || undefined}>
            {blocks.map((b) => (
              <li key={b.day} className="pa-thread-day">
                <h4 className="pa-thread-dayhead"><span>{dayHeading(b.day, now)}</span></h4>
                <ol>
                  {b.notes.map((n, i) => {
                    flat += 1;
                    const at = flat;
                    const joined = continues(b.notes[i - 1], n);
                    const src = SOURCE[n.source];
                    const strand = strandOf(n);
                    const divider = strand !== lastStrand;
                    lastStrand = strand;
                    const role = n.author.role && n.author.role !== src.label ? n.author.role : null;
                    const plain = n.kind === 'GENERAL' || n.kind === 'NOTE' || n.kind === 'UMED';
                    const mine = n.source === 'PA' && (n.author.id ? Boolean(userId) && n.author.id === userId : mineLocal.current.has(n.id));
                    return (
                      <React.Fragment key={n.id}>
                        {divider && (
                          <li className="pa-thread-strand">
                            <span className="pa-strand-tag" style={{ borderColor: src.color }}>
                              <Marker shape={src.marker} color={src.color} size={8} />
                              {n.source === 'PA' ? n.episode.label : `${src.label}${n.episode.label && n.episode.label !== src.label ? ` · ${n.episode.label}` : ''}`}
                            </span>
                          </li>
                        )}
                        {/* On touch the per-note action shows on one note at a time: the newest, or the one tapped. */}
                        <li className={`pa-msg${joined && !divider ? ' joined' : ''}${at === actIndex ? ' act-here' : ''}${foundId && foundId.id === n.id ? ' found' : ''}`}
                          data-note-id={n.id} tabIndex={foundId && foundId.id === n.id ? -1 : undefined}
                          onClick={mayWrite ? () => { if (at !== actIndex) setActAt(at); } : undefined}>
                          {joined && !divider
                            ? <span className="pa-msg-gutter" aria-hidden="true"><time dateTime={n.recorded_at}>{clock(n.recorded_at)}</time></span>
                            : mine
                              ? <span className="pa-avatar you" aria-hidden="true">You</span>
                              // One colour, one meaning: the avatar wears its note's desk (the key above); the initials say who.
                              : <span className="pa-avatar" aria-hidden="true" style={{ background: src.color }}>{initials(n.author.name)}</span>}
                          <div className="pa-msg-body">
                            {(!joined || divider) && (
                              <div className="pa-msg-meta">
                                <strong>{n.author.name || 'Unknown author'}</strong>
                                {role && <span className="pa-msg-role">{role}</span>}
                                <time dateTime={n.recorded_at} title={exact(n.recorded_at)}>
                                  {relativeTime(n.recorded_at, now)}<span className="pa-sr-only"> ({exact(n.recorded_at)})</span>
                                </time>
                                {!plain && <span className={`pa-chip-kind k-${n.kind.toLowerCase()}`}>{KIND_LABEL[n.kind] || n.kind}</span>}
                              </div>
                            )}
                            {/* A joined note has no meta row; a screen reader still hears who and when. */}
                            {joined && !divider && <span className="pa-sr-only">{n.author.name || 'Unknown author'}, {exact(n.recorded_at)}. </span>}
                            {joined && !divider && !plain && <span className={`pa-chip-kind k-${n.kind.toLowerCase()} solo`}>{KIND_LABEL[n.kind] || n.kind}</span>}
                            <p className="pa-msg-text"><Highlight text={n.note} q={q} /></p>
                            {mayWrite && (
                              <button type="button" className="pa-msg-act" tabIndex={at === actIndex ? 0 : -1}
                                ref={(el) => { actButtons.current[at] = el; }}
                                aria-label={`Correct with a new note: the note of ${exact(n.recorded_at)}${n.author.name ? ` by ${n.author.name}` : ''}. Up and down arrows move between notes.`}
                                onFocus={() => setActAt(at)}
                                onKeyDown={(e) => {
                                  if (e.key === 'ArrowUp') { e.preventDefault(); moveAct(at - 1); }
                                  else if (e.key === 'ArrowDown') { e.preventDefault(); moveAct(at + 1); }
                                  else if (e.key === 'Home') { e.preventDefault(); moveAct(0); }
                                  else if (e.key === 'End') { e.preventDefault(); moveAct(shown.length - 1); }
                                }}
                                onClick={() => {
                                  setText((t) => `${t ? `${t}\n` : ''}Correction to the note of ${exact(n.recorded_at)}${n.author.name ? ` by ${n.author.name}` : ''}: `);
                                  composer.current?.focus();
                                }}>Correct with a new note</button>
                            )}
                          </div>
                        </li>
                      </React.Fragment>
                    );
                  })}
                </ol>
              </li>
            ))}
            {(pending.length > 0 || boxShownReadOnly) && (
              <li className="pa-thread-day">
                <ol>
                  {boxShownReadOnly && (
                    <li className="pa-msg pending kept" data-unsent="box">
                      <span className="pa-avatar you" aria-hidden="true">You</span>
                      <div className="pa-msg-body">
                        <div className="pa-msg-meta">
                          <strong>You</strong>
                          <span className="pa-msg-state kept">Draft kept</span>
                        </div>
                        <p className="pa-msg-text">{text}</p>
                        <div className="pa-msg-retry" role="group" aria-label="Words from your note box">
                          <p className="pa-msg-hold">Words left in your note box. Adding notes is closed to you here, so they cannot be sent from this panel. Copy them, or discard them.</p>
                          <button type="button" className="pa-thread-chip" onClick={() => { try { navigator.clipboard?.writeText(text); } catch { /* the words are on screen */ } }}>Copy</button>
                          <button type="button" className="pa-thread-chip discard" onClick={discardBox}>Discard</button>
                        </div>
                      </div>
                    </li>
                  )}
                  {pending.map((p) => {
                    const copy = () => { try { navigator.clipboard?.writeText(p.note); } catch { /* the words are on screen */ } };
                    const what = p.state === 'sending' ? 'sending' : p.hold === 'editing' ? 'editing' : p.hold === 'kept' ? 'kept' : 'failed';
                    return (
                      <li key={p.key} className={`pa-msg pending ${what}`} data-unsent={what}>
                        <span className="pa-avatar you" aria-hidden="true">You</span>
                        <div className="pa-msg-body">
                          <div className="pa-msg-meta">
                            <strong>You</strong>
                            <span className={`pa-msg-state ${what}`}>
                              {what === 'sending' ? 'Sending…' : what === 'editing' ? 'Editing' : what === 'kept' ? 'Draft kept' : 'Not sent'}
                            </span>
                            {p.kind === 'VETTING' && <span className="pa-chip-kind k-vetting">{KIND_LABEL[p.kind]}</span>}
                          </div>
                          <p className="pa-msg-text">{p.note}</p>
                          {/* Fix round 11: these words could not be stored here (a full quota): on this screen only. */}
                          {storing && p.stored === false && <p className="pa-msg-hold pa-msg-unstored" data-item-unsaved>{NOT_STORED}</p>}
                          {what === 'failed' && (
                            <div className="pa-msg-retry" role="group" aria-label="Unsent note">
                              <p className="pa-notice stop">{p.error}</p>
                              {p.caution ? <p className="pa-msg-hold" data-item-caution>{p.partial ? CAUTION_PARTIAL : CAUTION} Retry looks for these words in the thread first.</p> : null}
                              <button type="button" className="pa-thread-chip" onClick={() => sendItem(p, 'retry')} aria-disabled={checkingKey === p.key || undefined}>{checkingKey === p.key ? 'Checking…' : 'Retry'}</button>
                              <button type="button" className="pa-thread-chip" onClick={copy}>Copy</button>
                              {mayWrite && <button type="button" className="pa-thread-chip" onClick={() => editPending(p)}>Edit</button>}
                              <button type="button" className="pa-thread-chip discard" onClick={() => discard(p)}>Discard</button>
                            </div>
                          )}
                          {what === 'kept' && (
                            <div className="pa-msg-retry" role="group" aria-label="Draft kept">
                              <p className="pa-msg-hold">{KEPT_WHY[p.why || 'edit']}</p>
                              {p.caution ? <p className="pa-msg-hold" data-item-caution>{p.partial ? CAUTION_PARTIAL : CAUTION} Send looks for these words in the thread first.</p> : null}
                              {mayWrite && <button type="button" className="pa-thread-chip" onClick={() => sendItem(p, 'kept')} aria-disabled={checkingKey === p.key || undefined}>{checkingKey === p.key ? 'Checking…' : 'Send'}</button>}
                              {mayWrite && <button type="button" className="pa-thread-chip" onClick={() => editPending(p)}>Edit</button>}
                              <button type="button" className="pa-thread-chip" onClick={copy}>Copy</button>
                              <button type="button" className="pa-thread-chip discard" onClick={() => discard(p)}>Discard</button>
                            </div>
                          )}
                          {what === 'editing' && (
                            <div className="pa-msg-retry" role="group" aria-label="Note being edited">
                              <p className="pa-msg-hold">{!(heldEdit && heldEdit.key === p.key)
                                // Fix round 9: this box does not hold it (another tab's box does): never "in the box below".
                                ? 'Being edited in another tab. It stays here, as it was, until it is sent or discarded. Sent, it goes as this same note, so it is never recorded twice.'
                                : mayWrite
                                  ? 'In the box below. It stays here, as it was, until you send or discard it. Sent, it goes as this same note, so it is never recorded twice.'
                                  : 'Your edit is kept with this note. Adding notes is closed to you on this case, so the box is hidden; open a case of this resident you can write on to send it, as this same note.'}</p>
                              <button type="button" className="pa-thread-chip" onClick={() => stopEditing(p)}>Stop editing</button>
                              <button type="button" className="pa-thread-chip discard" onClick={() => discard(p)}>Discard</button>
                            </div>
                          )}
                          {/* Fix round 10: a cautioned note's Send found the same words: it asks here, on the note. */}
                          {itemAsk && itemAsk.key === p.key && (what === 'kept' || what === 'failed') && (
                            <div className="pa-notice attn pa-same-ask" data-item-ask="same" role="group" aria-label="Send these words again?">
                              <p>{sameWords(itemAsk.same, false)}</p>
                              {(itemAsk.same.where === 'thread' || itemAsk.same.where === 'part') && <button type="button" className="pa-link" onClick={() => showNote((itemAsk.same as { noteId: string }).noteId)}>Show that note</button>}
                              {itemAsk.same.where === 'part' && itemAsk.same.rest && (
                                <button type="button" className="pa-thread-chip" onClick={() => { const rest = (itemAsk.same as { rest: string }).rest; setItemAsk(null); sendItemRest(p, what === 'kept' ? 'kept' : 'retry', rest); }}>Send only the new part</button>
                              )}
                              <button type="button" className="pa-thread-chip" onClick={() => { setItemAsk(null); sendItem(p, what === 'kept' ? 'kept' : 'retry', true); }}>{itemAsk.same.where === 'part' || itemAsk.same.where === 'partMissing' ? 'Send all' : 'Send anyway'}</button>
                              <button type="button" className="pa-thread-chip" onClick={() => { setItemAsk(null); setSaid('Not sent. The words are still in your unsent notes.'); }}>Don’t send</button>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </li>
            )}
          </ol>
        )}
        {unseen > 0 && (
          <div className="pa-thread-jumpwrap">
            <button type="button" className="pa-thread-jump" onClick={toBottom}>Jump to newest</button>
          </div>
        )}
      </div>

      {/*
        Fix round 8: every notice about the box is ALSO said in the one polite status line above (setSaid),
        which is always in the page; the notices themselves are not live regions, so nothing is read twice.
      */}
      {signedOut && signedOut.gone ? (
        <p className="pa-thread-readonly" data-box-note="signed-out">You signed out in another tab. The unsent words on this computer were deleted with the sign-out. Sign in again to write notes.</p>
      ) : mayWrite ? (
        <form className="pa-composer" aria-label="Add a note" onSubmit={(e) => { e.preventDefault(); trySend(); }}>
          {signedOut && (
            <p className="pa-notice stop" data-box-note="signed-out">You signed out in another tab, so nothing here is kept on this computer any more. Copy any words you need before you leave.</p>
          )}
          {sentHere && (
            <p className="pa-notice ok" data-box-note="sent">
              These words were sent from another tab, so they were not put back in the box.{' '}
              <button type="button" className="pa-link" onClick={() => showNote(sentHere.noteId)}>Show the note</button>{' '}
              <button type="button" className="pa-link" onClick={() => { setSentHere(null); composer.current?.focus(); }}>Dismiss</button>
            </p>
          )}
          {sameAsk && (
            <div className="pa-notice attn pa-same-ask" data-box-note="same" role="group" aria-label="Send these words again?">
              <p>{sameWords(sameAsk)}</p>
              {(sameAsk.where === 'thread' || sameAsk.where === 'part') && <button type="button" className="pa-link" onClick={() => showNote(sameAsk.noteId)}>Show that note</button>}
              {sameAsk.where === 'part' && sameAsk.rest && (
                <button type="button" className="pa-thread-chip" onClick={() => sendBoxRest(sameAsk.rest)}>Send only the new part</button>
              )}
              <button type="button" className="pa-thread-chip" onClick={() => { setSameAsk(null); trySend(true); }}>{sameAsk.where === 'part' || sameAsk.where === 'partMissing' ? 'Send all' : 'Send anyway'}</button>
              <button type="button" className="pa-thread-chip" onClick={() => { setSameAsk(null); setSaid('Not sent. Your words are still in the box.'); composer.current?.focus(); }}>Don’t send</button>
            </div>
          )}
          {/*
            Fix round 11: the caution and the note about what became of the box are ONE compact notice when both
            apply (a phone used to keep 33px of thread under the two): the caution's line, the why folded below it.
          */}
          {caution && !sameAsk && text.trim() && boxNote ? (
            <div className="pa-notice attn pa-notice-compact" data-box-note="caution both">
              <p>{cautionPartial ? CAUTION_PARTIAL : CAUTION}</p>
              <details>
                <summary>What happened</summary>
                <p>{boxNote} Send looks for these words in the thread first.</p>
              </details>
              <button type="button" className="pa-link" onClick={() => { setBoxNote(null); composer.current?.focus(); }}>Dismiss</button>
            </div>
          ) : (
            <>
              {caution && !sameAsk && text.trim() && (
                <p className="pa-notice attn" data-box-note="caution">{cautionPartial ? CAUTION_PARTIAL : CAUTION} Send looks for these words in the thread first.</p>
              )}
              {boxNote && (
                <p className="pa-notice attn" data-box-note="both">
                  {boxNote}{' '}
                  <button type="button" className="pa-link" onClick={() => { setBoxNote(null); composer.current?.focus(); }}>Dismiss</button>
                </p>
              )}
            </>
          )}
          {storing && ((unsaved && text.trim()) || unstoredItems) && (
            <p className="pa-notice attn" data-box-note="unsaved">{unsaved && text.trim() ? UNSAVED : UNSAVED_ITEMS}</p>
          )}
          <div className="pa-composer-row">
            <div className="pa-seg" role="group" aria-label="Note kind">
              <button type="button" aria-pressed={kind === 'GENERAL'} onClick={() => aimAt(targetCase, 'GENERAL')}>Note</button>
              <button type="button" aria-pressed={kind === 'VETTING'} onClick={() => aimAt(targetCase, 'VETTING')}>Vetting</button>
            </div>
            {writable.length > 1 && (
              <label className="pa-composer-target"><span>On</span>
                <select value={targetCase} onChange={(e) => aimAt(e.target.value, kind)} aria-label="Which case this note is on">
                  {writable.map((w) => <option key={w.case_id} value={w.case_id}>{w.label}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="pa-composer-box">
            <textarea id="pa-composer-text" ref={composer} value={text} rows={2} maxLength={8000} aria-label="Note text" aria-describedby="pa-composer-hint" placeholder="Write a note. Enter sends; Shift+Enter for a new line."
              onChange={(e) => onType(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); trySend(); } }} />
            <button type="submit" className="pa-send" disabled={!text.trim()} aria-disabled={checking || undefined}>{checking ? 'Checking…' : 'Send'}</button>
          </div>
          <p className="pa-composer-hint" id="pa-composer-hint">Kept forever, never overwritten. You stay where you are.</p>
        </form>
      ) : page && (
        <>
        {sentHere && (
          <p className="pa-notice ok" data-box-note="sent">
            These words were sent from another tab, so they were not put back in the box.{' '}
            <button type="button" className="pa-link" onClick={() => showNote(sentHere.noteId)}>Show the note</button>
          </p>
        )}
        <p className="pa-thread-readonly">{page.read_only ? 'Read-only: you no longer hold this resident’s open work.' : 'Adding notes is not open for your role here.'}</p>
        </>
      )}
    </section>
  );
}
